import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  FaExternalLinkAlt,
  FaStar,
  FaPhone,
  FaPaperclip,
  FaTimes,
  FaPlay,
  FaPause,
  FaPlus,
  FaShareAlt,
  FaRegThumbsUp,
  FaRegThumbsDown,
  FaThumbsUp,
  FaThumbsDown,
  FaFilePdf
} from 'react-icons/fa';
import { v4 as uuid_v4 } from 'uuid';
import CustomLoading from '../../util_components/custom_loading';
import CustomPlayer from '../../util_components/custom_video_react';
import SmsWindow from '../reports/sms_window';
import SearchableSelect from '../reminders/SearchableSelect';
import ReminderForm from '../reminders/ReminderForm';
import {
  create_task as create_reminder,
  current_team_member,
  prime_team_members,
  list_departments
} from '../reminders/api';
import { openChatWithUserUUID, sendMessageToUserUUID } from '../../chat/chat';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { getCookie } from '../../../util_functions';
import { get_api_url } from '../../../utils/urls';
import {
  downloadPersonalisedPlanPdf,
  generatePersonalisedPlanPdfBlob
} from '../../../utils/downloadPersonalisedPlanPdf';
import { get_api, post_api, api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { createdTimeLine } from './created_time';
import { default_agent_filter, filter_query_params, initial_filter_state } from './filters';
import InstantBanner from '../../util_components/instant_alert/InstantBanner';
import {
  CLAIM_HOLDING,
  CLAIM_MINE,
  CLAIM_PENDING,
  CLAIM_RELEASED,
  announce_if_gone,
  claim_blocks_form,
  claim_forces_terminal,
  claim_should_retry,
  claim_state_from_error,
  claim_state_from_response,
  initial_claim_state,
  visible_instant_expiry
} from '../../util_components/instant_alert/claim';
import { INSTANT_GONE_EVENT, last_pending_is_sales, sales_may_claim } from './instant_alert_config';
import { publish_claim_changed } from './claim_broadcast';
import {
  CLAIM_POLL_MS,
  hold_forces_read_only,
  hold_lapsed,
  released_toast,
  should_claim_any_on_open,
  shows_hold_banner,
  state_after_assign,
  state_from_claim_status
} from './claim_hold';
import { fetch_claim_status, fetch_me_once, send_task_chat, unclaim_task } from './claim_api';
import { chat_send_failure, chat_send_route } from './chat_send_route';
import { claim_gate, not_holder_message } from './claim_gate';
import { allocationClaimConfirm, formClaimStep } from './session_allocation/task_allocation';
import { resolveFilterTz } from './filter_tz';
import ClaimCountdownBanner from './ClaimCountdownBanner';
import OutcomePanel from './OutcomePanel';
import {
  build_complete_payload,
  build_skip_payload,
  is_outcome_required_error,
  label_for,
  outcome_options,
  skip_reason_options,
  validate_done
} from './outcome';
import AssignPicker from './AssignPicker';
import { can_assign as can_assign_tasks, url_level } from './level';
import { compose_outbound_message } from './onboarding_link';
import { details_age } from './details_age';
import { closed_by_line } from './closed_by';
import { lead_message_templates } from './lead_templates';
import { useOffHoursGate } from './OffHoursConfirm';
import { offHours, offHoursTooltip } from './off_hours';
import { PRIORITY_META, priority_of } from './priority';
import SkipDialog from './SkipDialog';
import { Banner, Btn, Chip, LABEL_CLASS, Ledger, SalesDialog, SectionCard, Segmented } from './ui';
import {
  comm_kind_chip,
  kind_level_label,
  rule_line_rest,
  sentiment_tone,
  session_status_tone,
  timeline_color
} from './form_view';
import { useIsMobile } from '../../../utils/responsive/viewport';
import { PHONE_MAX } from '../../../utils/responsive/breakpoints';
import { registerChatSendGuard } from '../../chat/p2p_chat_window/send_guard';
import {
  ai_note_payload,
  make_selection,
  record_ai_message,
  selection_after_edit,
  sms_payload
} from './ai_message_note';
import 'video-react/dist/video-react.css';

// The rich outbound-email composer shared with the Support Inbox ("New
// conversation"). Lazy so react-quill / emoji-mart only load when the operator
// actually opens the "Send email" popup.
const NewConversationModal = lazy(() => import('../support_inbox/new_conversation_modal'));

const LIST_PATH = '/sales-task-list';

// Same rich-text toolbar as the early-retention checklist's Review Notes.
const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean']
  ]
};
const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet', 'link'];

// Plain text of rich-text HTML (empty Quill is "<p><br></p>", which strips to '').
const strip_notes_html = (html) => (html || '').replace(/<[^>]*>/g, '').trim();

// True when the Review Notes carry text the agent actually wrote — i.e. more
// than the task tag the form seeds into an empty editor ("<p>#SalesTaskList -
// {task_label}</p>", see the hydrate effect). Completing a task with an
// untouched seed must not send notes_in_hridaya at all, or the backend would
// append a phantom "[Sales Task] {label}" note to the student. Mirrors
// hasManualNote in goserver (PR #803), the server-side half of this defense.
const notes_beyond_seed = (html, task_label) => {
  const text = strip_notes_html(html);
  if (text === '') return false;
  const seed = `#SalesTaskList - ${task_label || ''}`.trim();
  // The bare variant covers a seed created while task_label was still empty.
  return text !== seed && text !== '#SalesTaskList -';
};

/*
 * Query string for /next_task: the task on screen to skip over, plus the filters
 * that scope the walk.
 *
 * Opened from the list (?from=list) it sends the LIST's filters — resolved from
 * the same localStorage blob and the same defaults the list page uses
 * (filters.js) — so "Done and Next" stays inside the set the agent is browsing.
 * Without them the server answers with the globally earliest open task, which is
 * how a concierge filtered to their own list was handed another concierge's task.
 *
 * localStorage is what makes that work now that the list opens the form in a NEW
 * TAB: it is shared across tabs, where router state and redux are not. The one
 * filter it cannot carry is the type-to-search term, which is deliberately not
 * persisted (a search is momentary, not a working preference). The list therefore
 * puts an active search in the link as ?search=… (form_link in index.jsx) and it
 * is read back here, so a walk started from a searched list stays inside the
 * searched set instead of jumping to some other student. Absent param = no search
 * filter, which is exactly the pre-search behaviour.
 *
 * The list also puts its level, scope, L3 owner, call-eligibility filters and
 * header timezone in the link (?level=, ?scope=, ?owner=, ?call=, ?tz=). The level is never persisted,
 * and the URL copies of the others pin the walk to what the list showed when
 * the task was opened.
 *
 * From any other entry point (the student-profile popup, a bookmarked form URL)
 * there is no browsing context, so it falls back to the assignee scope alone —
 * unassigned + self — which is the minimum that keeps that bug from recurring.
 *
 * 'Submitted' is dropped from the status filter: an open task is by definition not
 * submitted, so sending it would guarantee "no more tasks". The server drops it
 * too; doing it here keeps the intent visible on the side that has the context.
 */
const next_task_query = (uuid) => {
  const self_uuid = getAuthUserInfoOrDefault().uuid || '';
  const query = new URLSearchParams(window.location.search);
  const from_list = query.get('from') === 'list';
  // The list's header zone (?tz=), so the rolling default and the server read the same days.
  const tz = query.get('tz') || '';
  const filters = from_list
    ? { ...initial_filter_state(tz), search: query.get('search') || '', tz }
    : { agent_filter: default_agent_filter(self_uuid) };
  filters.level = query.get('level') || '';
  if (query.has('scope')) filters.scope_filter = query.get('scope');
  if (query.has('owner')) filters.owner_filter = query.get('owner').split(',').filter(Boolean);
  if (query.has('call')) filters.call_filter = query.get('call');
  if (filters.status_filter === 'Submitted') filters.status_filter = '';
  const params = filter_query_params(filters);
  params.set('exclude_uuid', uuid);
  return params.toString();
};

// Message playbook: drafts are pre-generated server-side, so a request only
// ever waits on the poll loop (4s cadence, 120s before we give up). While a
// pair is on screen a cheap `peek` re-check catches mid-session invalidation.
const PLAYBOOK_POLL_MS = 4000;
const PLAYBOOK_POLL_LIMIT_MS = 120000;
const PLAYBOOK_PEEK_MS = 60000;
const PLAYBOOK_URL = '/v2/sales_task_list/message_playbook';

// Link look for names and in-page links (rule 5: link token, 600).
const LINK_CLASS = 'font-semibold text-content-link hover:underline';

// Row-level button that reads as a link (the CMS's global button rule is reset inline).
const LINK_BTN_STYLE = {
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer'
};

// ── Membership timeline (ported from the ERC form, extended event kinds) ────
function MembershipTimeline({ events, next_renewal, sessions_remaining }) {
  const has_sessions = typeof sessions_remaining === 'number';
  if ((events || []).length === 0 && !next_renewal && !has_sessions) {
    return <div className="px-3.5 py-3 text-xs text-content-muted">No membership events.</div>;
  }
  return (
    <div className="flex flex-col gap-3 px-3.5 py-3">
      {has_sessions || next_renewal ? (
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          {has_sessions ? (
            <span>
              <span className="text-content-muted">Sessions remaining </span>
              <span className="font-semibold text-content-primary">{sessions_remaining}</span>
            </span>
          ) : null}
          {next_renewal ? (
            <span>
              <span className="text-content-muted">Next renewal date </span>
              <span className="font-semibold text-content-primary">{next_renewal}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      {(events || []).length ? (
        <ol
          className="m-0 grid list-none gap-x-1.5 gap-y-3 p-0 text-[12.5px]"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
        >
          {events.map((e, idx) => (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              className="pt-1.5"
              style={{ borderTop: `3px solid ${timeline_color(e.type)}` }}
            >
              {/* Event title, plan name and prepay term; `detail` stands in for non-plan events. */}
              <div className="font-semibold text-content-primary">
                {e.title}
                {e.plan_name ? <> — {e.plan_name}</> : null}
              </div>
              {e.prepay ? (
                <div className="mt-0.5">
                  <Chip tone="warning">{e.prepay}</Chip>
                </div>
              ) : null}
              {!e.plan_name && e.detail ? (
                <div className="text-xs text-content-secondary">{e.detail}</div>
              ) : null}
              <div className="text-xs text-content-muted">{e.date}</div>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
MembershipTimeline.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  next_renewal: PropTypes.string,
  sessions_remaining: PropTypes.number
};
MembershipTimeline.defaultProps = { events: [], next_renewal: '', sessions_remaining: undefined };

// Teacher name, hyperlinked to the teacher profile (new tab) when a uuid is
// present. Falls back to plain text / em dash.
const teacher_link = (s) => {
  const name = s.teacher_name || '—';
  if (!s.teacher_uuid) return name;
  return (
    <Link
      to={`/teacherprofile?id=${encodeURIComponent(s.teacher_uuid)}`}
      target="_blank"
      rel="noopener noreferrer"
      className={LINK_CLASS}
    >
      {name}
    </Link>
  );
};

// PC and GC detail pages take the uuid under different query params.
const session_details_url = (s) =>
  s.kind === 'GC'
    ? `/groupsessiondetails?session_uuid=${encodeURIComponent(s.session_uuid)}`
    : `/sessiondetails?id=${encodeURIComponent(s.session_uuid)}`;

// One session line: when (links to the session page) · what · status chip, and
// the student's rating and review beneath when there is one.
function SessionRow({ s }) {
  const is_gc = s.kind === 'GC';
  return (
    <div className="py-1.5 text-xs">
      {/* The start time carries two zones, so it gets its own line instead of a narrow column. */}
      <div
        className="grid items-start gap-x-2 gap-y-0.5"
        style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}
      >
        {s.session_uuid ? (
          <a
            href={session_details_url(s)}
            target="_blank"
            rel="noopener noreferrer"
            className="tabular-nums text-content-link hover:underline"
          >
            {s.start_time || '—'}
          </a>
        ) : (
          <span className="tabular-nums text-content-muted">{s.start_time || '—'}</span>
        )}
        <Chip tone={session_status_tone(s.status)}>{s.status || '—'}</Chip>
        <span className="col-span-2 min-w-0 break-words text-content-primary">
          <span className="font-semibold text-content-secondary">{s.kind}</span>
          {' · '}
          {is_gc ? (
            <>
              {s.class_name || 'Group class'}
              {s.teacher_name ? <> — {teacher_link(s)}</> : null}
            </>
          ) : (
            teacher_link(s)
          )}
          {!is_gc && s.duration ? (
            <span className="text-content-muted"> · {s.duration} min</span>
          ) : null}
        </span>
      </div>
      {s.rating ? (
        <div className="mt-0.5 flex items-start gap-1 text-content-muted">
          <span
            className="inline-flex shrink-0 items-center pt-0.5"
            aria-label={`${s.rating} of 5`}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <FaStar
                key={n}
                size={10}
                style={{ color: n <= s.rating ? 'var(--state-warning)' : 'var(--border-default)' }}
              />
            ))}
          </span>
          {s.review ? <span className="italic">“{s.review}”</span> : null}
        </div>
      ) : null}
    </div>
  );
}
SessionRow.propTypes = { s: PropTypes.object.isRequired };

function SessionList({ title, list, view_all_url, view_all_label }) {
  return (
    <div className="min-w-0 px-3.5 py-2.5">
      <div className="mb-1 flex items-center gap-2">
        <span className={LABEL_CLASS}>{title}</span>
        {view_all_url ? (
          <a
            href={view_all_url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-xs text-content-link hover:underline"
          >
            {view_all_label}
          </a>
        ) : null}
      </div>
      {!list || list.length === 0 ? (
        <div className="py-1 text-xs text-content-muted">No sessions.</div>
      ) : (
        list.map((s, idx) => <SessionRow key={s.session_uuid || idx} s={s} />)
      )}
    </div>
  );
}
SessionList.propTypes = {
  title: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(PropTypes.object),
  view_all_url: PropTypes.string,
  view_all_label: PropTypes.string
};
SessionList.defaultProps = { list: [], view_all_url: '', view_all_label: '' };

// mm:ss formatter for the recording timer.
const fmt_secs = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};

// Inline call-recording player: play/pause, seek slider, timer. The signed URL
// is fetched lazily on first play (it expires, so we don't mint it up front).
function CallRecordingPlayer({ callUuid, fetchRecording, onError }) {
  const audio_ref = useRef(null);
  const want_play_ref = useRef(false);
  const [url, set_url] = useState('');
  const [loading, set_loading] = useState(false);
  const [playing, set_playing] = useState(false);
  const [cur, set_cur] = useState(0);
  const [dur, set_dur] = useState(0);

  const toggle = () => {
    const a = audio_ref.current;
    if (url && a) {
      if (playing) a.pause();
      else a.play();
      return;
    }
    if (loading) return;
    set_loading(true);
    fetchRecording(callUuid)
      .then((u) => {
        set_loading(false);
        if (!u) {
          onError('No recording available for this call');
          return;
        }
        want_play_ref.current = true; // autoplay once the audio can play
        set_url(u);
      })
      .catch(() => {
        set_loading(false);
        onError('Failed to load recording');
      });
  };

  const seek = (e) => {
    const a = audio_ref.current;
    const t = Number(e.target.value);
    if (a) a.currentTime = t;
    set_cur(t);
  };

  return (
    <div className="mt-1.5 flex items-center gap-2" style={{ maxWidth: 360 }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 28,
          width: 28,
          borderRadius: 9999,
          border: '1px solid var(--brand-primary)',
          background: 'var(--brand-primary)',
          color: 'var(--text-on-brand)',
          padding: 0,
          margin: 0,
          cursor: loading ? 'wait' : 'pointer',
          flexShrink: 0
        }}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {loading ? '…' : playing ? <FaPause size={11} /> : <FaPlay size={11} />}
      </button>
      <input
        type="range"
        className="stl-range"
        min={0}
        // Positive fallback until metadata loads — with max=0 the thumb renders
        // mid-track with a sliver; cur/1 = 0% keeps it hard-left instead.
        max={dur > 0 ? dur : 1}
        step="0.1"
        value={cur}
        onChange={seek}
        // The .stl-range class (common.css) strips the leaked global input
        // border/box-shadow/outline pill across hover/focus states.
        style={{ flex: 1, accentColor: 'var(--brand-primary)', cursor: 'pointer', margin: 0 }}
      />
      <span className="shrink-0 text-xs tabular-nums text-content-muted">
        {fmt_secs(cur)} / {fmt_secs(dur)}
      </span>
      {url ? (
        <audio
          ref={audio_ref}
          src={url}
          preload="metadata"
          style={{ display: 'none' }}
          onLoadedMetadata={(e) => set_dur(e.target.duration || 0)}
          onCanPlay={() => {
            if (want_play_ref.current && audio_ref.current) {
              want_play_ref.current = false;
              audio_ref.current.play();
            }
          }}
          onTimeUpdate={(e) => set_cur(e.target.currentTime || 0)}
          onPlay={() => set_playing(true)}
          onPause={() => set_playing(false)}
          onEnded={() => {
            set_playing(false);
            set_cur(0);
          }}
        />
      ) : null}
    </div>
  );
}
CallRecordingPlayer.propTypes = {
  callUuid: PropTypes.string.isRequired,
  fetchRecording: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};

// Call durations as the form has always shown them.
const call_duration = (secs) => `${Math.round(secs / 60)}m ${secs % 60}s`;

// "Recent communications": merged chat / SMS / WhatsApp / call / session rows,
// newest first. Calls open a transcript panel and play their recording inline.
function RecentCommunications({
  events,
  onOpenTranscript,
  fetchRecording,
  onPlaySession,
  onError
}) {
  if (!events || events.length === 0) {
    return <div className="px-3.5 py-3 text-xs text-content-muted">No recent communications.</div>;
  }
  return (
    <div className="text-[12.5px]">
      {events.map((e, idx) => {
        const chip = comm_kind_chip(e.kind);
        const inbound = e.direction === 'in';
        const is_call = e.kind === 'call';
        const can_play_session = e.kind === 'session' && e.has_recording && e.session_uuid;
        let action = null;
        if (is_call && e.transcript) {
          action = (
            <Btn size="row" onClick={() => onOpenTranscript(e)}>
              Transcript
            </Btn>
          );
        } else if (can_play_session) {
          action = (
            <Btn size="row" onClick={() => onPlaySession(e.session_uuid)}>
              <FaPlay size={9} /> Play recording
            </Btn>
          );
        }
        let direction = '';
        if (e.kind !== 'session' && e.direction) direction = inbound ? 'From member' : 'To member';
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            className="grid items-start gap-x-2.5 gap-y-1 px-3.5 py-2.5 max-sm:grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[96px_minmax(0,1fr)_auto]"
            style={idx ? { borderTop: '1px solid var(--border-subtle)' } : undefined}
          >
            <span className="justify-self-start">
              <Chip tone={chip.tone}>
                {chip.label}
                {is_call && e.duration ? ` · ${call_duration(e.duration)}` : ''}
              </Chip>
            </span>
            <div className="min-w-0 max-sm:col-span-2 max-sm:row-start-2">
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {e.title ? (
                  // Session titles link to the session page; everything else is plain text.
                  e.kind === 'session' && e.session_uuid ? (
                    <a
                      href={session_details_url({
                        kind: e.session_kind,
                        session_uuid: e.session_uuid
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={LINK_CLASS}
                    >
                      {e.title}
                    </a>
                  ) : (
                    <span className="font-semibold text-content-primary">{e.title}</span>
                  )
                ) : null}
                {e.session_kind ? (
                  <span className="text-xs font-semibold text-content-secondary">
                    {e.session_kind}
                  </span>
                ) : null}
                <span className="text-xs text-content-muted">
                  {[direction, e.date, e.status].filter(Boolean).join(' · ')}
                </span>
              </div>
              {e.teacher_name ? (
                <div className="text-xs text-content-secondary">
                  with{' '}
                  {e.teacher_uuid ? (
                    <a
                      href={`/teacherprofile?id=${encodeURIComponent(e.teacher_uuid)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={LINK_CLASS}
                    >
                      {e.teacher_name}
                    </a>
                  ) : (
                    e.teacher_name
                  )}
                </div>
              ) : null}
              {e.body ? (
                <div
                  className="mt-0.5 text-content-primary"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {e.body}
                </div>
              ) : null}
              {is_call && (e.sentiment || e.talk_ratio_customer || e.talk_ratio_agent) ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {e.sentiment ? (
                    <span className="capitalize">
                      <Chip tone={sentiment_tone(e.sentiment)}>{e.sentiment}</Chip>
                    </span>
                  ) : null}
                  {e.talk_ratio_customer || e.talk_ratio_agent ? (
                    <Chip tone="neutral">
                      customer / agent - {Math.round(e.talk_ratio_customer || 0)} /{' '}
                      {Math.round(e.talk_ratio_agent || 0)}
                    </Chip>
                  ) : null}
                </div>
              ) : null}
              {is_call && Array.isArray(e.concerns) && e.concerns.length > 0 ? (
                <ul className="mb-0 mt-1 list-disc pl-5 text-xs text-content-secondary">
                  {e.concerns.map((c, ci) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={ci}>{c}</li>
                  ))}
                </ul>
              ) : null}
              {is_call && e.has_recording && e.call_uuid ? (
                <CallRecordingPlayer
                  callUuid={e.call_uuid}
                  fetchRecording={fetchRecording}
                  onError={onError}
                />
              ) : null}
            </div>
            <span className="justify-self-end max-sm:col-start-2 max-sm:row-start-1">{action}</span>
          </div>
        );
      })}
    </div>
  );
}
RecentCommunications.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  onOpenTranscript: PropTypes.func.isRequired,
  fetchRecording: PropTypes.func.isRequired,
  onPlaySession: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};
RecentCommunications.defaultProps = { events: [] };

// Copy to the clipboard. The async Clipboard API is unavailable on an insecure
// origin, which some CMS machines still hit, so the hidden-textarea fallback is
// not optional here (same shape as the Support Inbox's copy helper).
const copy_to_clipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) resolve();
      else reject(new Error('copy command failed'));
    } catch (e) {
      reject(e);
    }
  });
};

// A lead's phone has never passed OTP — it is whatever they typed on the landing
// page. Q6 permits SMS and WhatsApp to it anyway, so this badge is the only
// signal the agent gets that the number may simply be wrong.
function PhoneUnverifiedBadge() {
  return <Chip tone="warning">Phone unverified</Chip>;
}

/*
 * The continue-onboarding link, between the suggestions and the message box —
 * where the agent is already looking when they compose.
 *
 * Read-only on purpose: the URL is minted and tracked server-side (clicks are
 * attributed by tag), so an edited one would be untracked and probably broken.
 * Copy exists because the agent may want to paste it into a channel this form
 * does not send through; every send path appends it on its own.
 *
 * `now` comes from the form's single clock so the expiry turns red as it passes
 * rather than at the next mount.
 */
function OnboardingLinkRow({ link, expires_at_iso, now }) {
  const [copied, set_copied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => set_copied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  // IST, like every other date on this page: the link's expiry is a UTC instant
  // and a rep west of Greenwich would otherwise be shown the previous day.
  const expires = expires_at_iso ? moment(expires_at_iso).tz('Asia/Kolkata') : null;
  const has_expiry = !!expires && expires.isValid();
  const expired = has_expiry && expires.diff(moment(now)) <= 0;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
      <span className="text-content-muted">Onboarding link</span>
      <input
        type="text"
        value={link}
        readOnly
        aria-label="Onboarding link"
        onClick={(e) => e.target.select()}
        className="min-w-0 rounded-md font-mono text-[11.5px] !h-7"
        // The CMS's global input rule forces 46px with !important.
        style={{
          flex: '1 1 140px',
          padding: '0 8px',
          border: '1px solid var(--border-subtle)',
          background: 'var(--surface-sunken)',
          color: 'var(--text-secondary)',
          margin: 0
        }}
      />
      <Btn
        size="row"
        onClick={() =>
          copy_to_clipboard(link)
            .then(() => set_copied(true))
            .catch(() => set_copied(false))
        }
      >
        {copied ? 'Copied!' : 'Copy'}
      </Btn>
      {has_expiry ? (
        <span style={{ color: expired ? 'var(--state-error-text)' : 'var(--text-muted)' }}>
          valid until {expires.format('D MMM YYYY')}
        </span>
      ) : null}
    </div>
  );
}
OnboardingLinkRow.propTypes = {
  link: PropTypes.string.isRequired,
  expires_at_iso: PropTypes.string,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.object])
};
OnboardingLinkRow.defaultProps = { expires_at_iso: '', now: undefined };

/*
 * The lead task's replacement for "Generate suggestions".
 *
 * Antaryami builds its prompt from a student id and a lead has no student row,
 * so there is nothing to generate (Q7). Two written openers, filled into the
 * same textarea the suggestions would have filled, keep the send buttons below
 * working exactly as they do on a member task.
 */
function LeadTemplates({ options, on_select }) {
  return (
    <div className="flex flex-col gap-2">
      <div className={LABEL_CLASS}>Message templates</div>
      <div className="text-xs text-content-muted">
        AI suggestions need a member profile, which this lead does not have yet.
      </div>
      {options.map((option) => (
        <div key={option.key} className="rounded-lg border border-line-subtle px-3 py-2.5">
          <div className={`mb-1 ${LABEL_CLASS}`}>{option.label}</div>
          <div className="mb-2 whitespace-pre-wrap text-[12.5px] text-content-primary">
            {option.text}
          </div>
          <Btn size="row" variant="outline" onClick={() => on_select(option.text)}>
            Use this
          </Btn>
        </div>
      ))}
    </div>
  );
}
LeadTemplates.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    })
  ).isRequired,
  on_select: PropTypes.func.isRequired
};

// ── Churned-member section ──────────────────────────────────────────────────
// Ported from the early-retention checklist form (render_pill,
// render_kind_badge, render_consistency_repeats, render_consistency_table,
// render_field_row / render_readonly_field) so a churned member's auto values
// read identically in both forms. Everything here is read-only.

// The auto count fields rendered as a stat strip at the top of the "Sessions"
// card (in this order), as the checklist does.
const CHURN_BADGE_FIELDS = [
  { key: 'pc_finished', label: 'Number of PC Finished', shape: 'sessions_hours' },
  { key: 'pc_finished_as_per_plan_v2', label: 'PC Finished as Per Plan' },
  { key: 'cancelled_noshow_pc', label: 'Cancelled / Cancelled within 12 hours / No Show (PC)' },
  { key: 'group_classes_finished', label: 'Number of GC Finished' },
  { key: 'cancelled_noshow_gc', label: 'Cancelled / No Show (GC)' }
];

// One stat: muted label, emphasised value. pc_finished is stored as
// {sessions, hours} — the strip shows the bare session count, as the checklist does.
function ChurnStatBadge({ field, churn }) {
  const raw = churn[field.key];
  const value = field.shape === 'sessions_hours' ? raw && raw.sessions : raw;
  const empty = value === undefined || value === null || value === '';
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-line-subtle bg-surface-sunken px-2.5 py-1">
      <span className="text-xs text-content-muted">{field.label}</span>
      <span className="text-[13px] font-semibold text-content-primary">
        {empty ? '—' : String(value)}
      </span>
    </div>
  );
}
ChurnStatBadge.propTypes = {
  field: PropTypes.object.isRequired,
  churn: PropTypes.object.isRequired
};

// The churned member's session counts, shown above the last-5 / next-5 lists.
function ChurnSessionBadges({ churn }) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-line-subtle px-3.5 py-2.5">
      {CHURN_BADGE_FIELDS.map((f) => (
        <ChurnStatBadge key={f.key} field={f} churn={churn} />
      ))}
    </div>
  );
}
ChurnSessionBadges.propTypes = { churn: PropTypes.object.isRequired };

// Repeats cell: count + one "day time - teacher - Nx" line per slot.
function ChurnRepeats({ repeats }) {
  const list = Array.isArray(repeats) ? repeats : [];
  if (list.length === 0) {
    return <span className="text-content-muted">—</span>;
  }
  return (
    <div className="text-xs text-content-primary">
      <div className="font-medium">{list.length}</div>
      {list.map((r, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={idx} className="text-content-muted">
          {r.day_time}
          {r.teacher_name ? (
            <>
              {' - '}
              {r.teacher_uuid ? (
                <Link
                  to={`/teacherprofile?id=${encodeURIComponent(r.teacher_uuid)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                >
                  {r.teacher_name}
                </Link>
              ) : (
                r.teacher_name
              )}
            </>
          ) : null}
          {r.times ? ` - ${r.times}x` : ''}
        </div>
      ))}
    </div>
  );
}
ChurnRepeats.propTypes = { repeats: PropTypes.arrayOf(PropTypes.object) };
ChurnRepeats.defaultProps = { repeats: [] };

// Teacher / class list with completed, scheduled and repeat-slot counts. The
// checklist's manual "Primary" checkbox column is dropped — this view is auto.
// Stacks into one card per row on phones so nothing scrolls sideways.
function ChurnConsistencyTable({ items }) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return (
      <div className="px-3.5 py-3 text-xs text-content-muted">
        No teachers or classes found for this member.
      </div>
    );
  }
  return (
    <div className="px-3.5 py-2 text-[12.5px]">
      <div
        className={`hidden gap-3 pb-1 sm:grid ${LABEL_CLASS}`}
        style={{ gridTemplateColumns: 'minmax(0,2fr) 80px 80px minmax(0,2fr)' }}
      >
        <span>Teacher / Class</span>
        <span>Completed</span>
        <span>Scheduled</span>
        <span>Repeats</span>
      </div>
      {list.map((it) => (
        <div
          key={`${it.kind}-${it.uuid}`}
          className="grid gap-x-3 gap-y-1 border-t border-line-subtle py-1.5 max-sm:grid-cols-2 sm:grid-cols-[minmax(0,2fr)_80px_80px_minmax(0,2fr)]"
        >
          <span className="min-w-0 max-sm:col-span-2">
            <Chip tone={it.kind === 'GC' ? 'brand' : 'info'}>{it.kind || 'PC'}</Chip>{' '}
            {it.kind === 'GC' ? (
              <span className="text-content-primary">
                {it.teacher_name || '—'}
                {it.name ? <span className="text-content-muted"> · {it.name}</span> : null}
              </span>
            ) : (
              <span className="text-content-primary">{it.name || '—'}</span>
            )}
          </span>
          <span className="text-content-primary">
            <span className="text-content-muted sm:hidden">Completed </span>
            {it.completed}
          </span>
          <span className="text-content-primary">
            <span className="text-content-muted sm:hidden">Scheduled </span>
            {it.scheduled}
          </span>
          <span className="max-sm:col-span-2">
            <span className="text-xs text-content-muted sm:hidden">Repeats </span>
            <ChurnRepeats repeats={it.repeats} />
          </span>
        </div>
      ))}
    </div>
  );
}
ChurnConsistencyTable.propTypes = { items: PropTypes.arrayOf(PropTypes.object) };
ChurnConsistencyTable.defaultProps = { items: [] };

const churn_empty = (v) => v === undefined || v === null || v === '';

// The early-retention values that are not part of the Sessions stat strip.
// Only rendered when the server flags the student as churned (auto_data
// .is_churned_member); membership_cancellation_date is hidden when unknown,
// matching the checklist.
function ChurnedMemberSection({ churn, student_uuid }) {
  const has_3m_plan = churn.teacher_created_3m_plan === 'Yes';
  const rows = [
    [
      'Teacher created 3 month plan',
      has_3m_plan && student_uuid ? (
        <Link
          to={`/personalised-plan?student_uuid=${encodeURIComponent(student_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={LINK_CLASS}
        >
          View 3 Month Plan
        </Link>
      ) : (
        churn.teacher_created_3m_plan || '—'
      )
    ]
  ];
  if (!churn_empty(churn.membership_cancellation_date)) {
    rows.push(['Membership cancellation date', churn.membership_cancellation_date]);
  }
  return (
    <>
      <SectionCard title="Early retention values (auto)">
        <div className="flex flex-col gap-2.5 px-3.5 py-3">
          {churn.anchor_date || churn.pause_days || churn.membership_paused ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {churn.anchor_date ? <Chip tone="neutral">As of {churn.anchor_date}</Chip> : null}
              {churn.pause_days ? <Chip tone="neutral">Pause Days +{churn.pause_days}</Chip> : null}
              {churn.membership_paused ? <Chip tone="warning">Membership Paused</Chip> : null}
            </div>
          ) : null}
          <Ledger rows={rows} labelWidth={190} />
        </div>
      </SectionCard>
      <SectionCard title="Teachers & classes (auto)">
        <ChurnConsistencyTable items={churn.consistency_items} />
      </SectionCard>
    </>
  );
}
ChurnedMemberSection.propTypes = {
  churn: PropTypes.object.isRequired,
  student_uuid: PropTypes.string
};
ChurnedMemberSection.defaultProps = { student_uuid: '' };

// The sales task form: a header card (who, which rule, hold, timing), read-only
// context cards, and the work panel (contact, messages, notes, outcome, footer).
//
// Data comes from /v2/sales_task_list/form/:uuid — auto_data is the
// server-computed payload frozen when the task was generated ({student,
// last_1on1_session, ai_getting_to_know}).
const TALK_TIME_REFRESH_MS = 5000;

function SalesTaskForm({ uuid, show_back_link }) {
  const [form, set_form] = useState(null);
  const [loading, set_loading] = useState(true);
  const [error, set_error] = useState('');

  // refresh=1 asks the server to recompute auto_data (open tasks only).
  const fetch_form = useCallback(
    (refresh = false) =>
      get_api(
        get_api_url(
          `/v2/sales_task_list/form/${encodeURIComponent(uuid)}${refresh ? '?refresh=1' : ''}`
        ),
        true
      ).then((res) => res?.data?.data || null),
    [uuid]
  );

  useEffect(() => {
    set_loading(true);
    set_error('');
    fetch_form()
      .then((data) => {
        set_form(data);
        set_loading(false);
      })
      .catch((e) => {
        set_error(e?.response?.data?.reason || 'Failed to load sales task');
        set_loading(false);
      });
  }, [fetch_form]);

  // Silent: the page stays mounted; a failure keeps what is shown.
  const reload_form = useCallback(() => {
    fetch_form()
      .then((data) => data && set_form(data))
      .catch(() => {});
  }, [fetch_form]);

  // Claim-on-open. Opening any open task claims it for this rep (for an instant
  // task that is also what clears every other rep's pop-up), so it fires as soon
  // as the payload loads, not on any user action. Guarded by uuid so a re-render
  // (or the per-second clock below) cannot send a second claim; "Done and Next"
  // and Reopen remount the form.
  const [claim, set_claim] = useState(initial_claim_state);
  const claimed_uuid_ref = useRef('');
  const permissions_status = useSelector((st) => st.loading.permissions_status);
  const access_type = useSelector((st) => st.home.access_type);
  // /me decides whether the banner offers Assign; cached per page session.
  const [sales_me, set_sales_me] = useState(null);
  useEffect(() => {
    let live = true;
    fetch_me_once()
      .then((m) => live && set_sales_me(m))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  // The effect below re-runs when may_claim flips (permissions or /me resolving).
  const may_claim = sales_may_claim(sales_me, last_pending_is_sales(), {
    permissions_status,
    access_type
  });
  const dispatch = useDispatch();
  const notify = useCallback(
    (type, message) => dispatch(actions.set_notification_variable(true, type, message)),
    [dispatch]
  );
  const me_uuid = getAuthUserInfoOrDefault().uuid || '';
  // Merge only the snapshot so typed notes, outcome and claim state survive.
  const [refreshing_details, set_refreshing_details] = useState(false);
  const refresh_details = useCallback(() => {
    set_refreshing_details(true);
    fetch_form(true)
      .then((data) => {
        if (!data) return;
        set_form((prev) =>
          prev
            ? {
                ...prev,
                auto_data: data.auto_data,
                auto_data_computed_at_iso: data.auto_data_computed_at_iso
              }
            : data
        );
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Could not refresh details'))
      .finally(() => set_refreshing_details(false));
  }, [fetch_form, notify]);
  const announce_claim_changed = useCallback(() => publish_claim_changed(uuid), [uuid]);
  // Also the claim gate's Retry, which asks the server even when may_claim is false.
  const run_claim = useCallback(() => {
    claimed_uuid_ref.current = uuid;
    set_claim((prev) => ({ ...prev, state: CLAIM_PENDING }));
    const send = () => post_api(get_api_url('/v2/sales_task_list/claim'), { uuid }, true);
    // CLAIM_RETRY gets exactly one more attempt; a second one fails open.
    send()
      .catch((e) => (claim_should_retry(e) ? send() : Promise.reject(e)))
      .then((res) => {
        const next = claim_state_from_response(res);
        set_claim(next);
        const toast = released_toast(next.released_task);
        if (toast) {
          notify('info', toast);
          announce_claim_changed();
        }
      })
      .catch((e) => {
        const next = claim_state_from_error(e);
        announce_if_gone(uuid, next, INSTANT_GONE_EVENT);
        set_claim(next);
      });
  }, [uuid, notify, announce_claim_changed]);
  // PROD-9977: a session someone else is joining asks before the claim; declining leaves it unclaimed.
  const [alloc_decision, set_alloc_decision] = useState(null);
  const claim_step = formClaimStep(
    form,
    me_uuid,
    should_claim_any_on_open(form) && may_claim,
    alloc_decision
  );
  const alloc_lock = claim_step === 'confirm' || claim_step === 'hold';
  const alloc_copy = useMemo(
    () =>
      allocationClaimConfirm(
        form,
        me_uuid,
        false,
        resolveFilterTz(new URLSearchParams(window.location.search).get('tz'))
      ),
    [form, me_uuid]
  );
  // Esc, backdrop and X only decline; closing the tab is the explicit button's job.
  const decline_allocation_claim = useCallback(() => set_alloc_decision('declined'), []);
  const leave_for_allocated = useCallback(() => {
    set_alloc_decision('declined');
    // Only closes a tab the list opened; otherwise the hold banner says to close it.
    window.close();
  }, []);
  const accept_allocation_claim = useCallback(() => set_alloc_decision('accepted'), []);
  useEffect(() => {
    if (claim_step !== 'claim') return;
    if (claimed_uuid_ref.current === uuid) return;
    run_claim();
  }, [claim_step, uuid, run_claim]);

  const may_assign = can_assign_tasks(sales_me, access_type);

  // While held, poll the server: only it knows of activity that extended the
  // hold, a release, or another claim. Results are dropped once the form has
  // left CLAIM_MINE so a late poll cannot undo a Release or an Assign. The ref
  // gives the poll the current state without a side effect inside an updater.
  const claim_state = claim.state;
  const claim_state_ref = useRef(claim_state);
  claim_state_ref.current = claim_state;
  const poll_claim = useCallback(
    (options = {}) =>
      fetch_claim_status(uuid)
        .then((poll) => {
          if (!options.any_state && claim_state_ref.current !== CLAIM_MINE) return;
          const next = state_from_claim_status(poll, me_uuid);
          claim_state_ref.current = next.state;
          set_claim(next);
          if (next.state !== CLAIM_MINE) announce_claim_changed();
        })
        .catch(() => {}),
    [uuid, me_uuid, announce_claim_changed]
  );
  useEffect(() => {
    if (claim_state !== CLAIM_MINE) return undefined;
    const id = setInterval(() => poll_claim(), CLAIM_POLL_MS);
    const on_visible = () => {
      if (document.visibilityState === 'visible') poll_claim();
    };
    document.addEventListener('visibilitychange', on_visible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', on_visible);
    };
  }, [claim_state, poll_claim]);

  const [releasing, set_releasing] = useState(false);
  const release_claim = () => {
    set_releasing(true);
    unclaim_task(uuid)
      .then(() => {
        claim_state_ref.current = CLAIM_RELEASED;
        set_claim((prev) => ({ ...prev, state: CLAIM_RELEASED, claim_expires_at_iso: null }));
        notify('success', 'Released back to the pool');
        announce_claim_changed();
      })
      .catch((e) => {
        notify('error', e?.response?.data?.reason || 'Could not release the task');
        // NOT_HOLDER: the hold already moved; show where it went.
        if (e?.response?.status === 409) poll_claim({ any_state: true });
      })
      .finally(() => set_releasing(false));
  };
  const on_assigned = (res, member) => {
    const next = state_after_assign(res, me_uuid);
    claim_state_ref.current = next.state;
    set_claim(next);
    notify('success', `Assigned to ${res?.claimed_by_name || member?.name || 'rep'}`);
    announce_claim_changed();
  };
  // Same URL, fresh location.state: form_page keys the form on it, so this
  // remounts the form and claims again.
  const router_history = useHistory();
  const router_location = useLocation();
  const reopen = () =>
    router_history.replace(`${router_location.pathname}${router_location.search}`, {
      reopen: Date.now()
    });

  // Live clock ticking each second so the member's current local time stays
  // current while the form is open.
  const [now, set_now] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => set_now(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Layout: phones (<640) get one column with the contact row in the header and
  // a fixed footer; wide screens (1280+) keep the work panel beside the context.
  const is_phone = useIsMobile(PHONE_MAX - 1);
  const is_wide = !useIsMobile(1279);
  const [message_tab, set_message_tab] = useState('student');

  // At the local zero, ask the server once per expiry: activity it has seen
  // since the last poll may have moved the hold.
  const lapsed = hold_lapsed(claim, now.getTime());
  const lapse_polled_ref = useRef('');
  useEffect(() => {
    if (!lapsed) return;
    const key = claim.claim_expires_at_iso || '';
    if (lapse_polled_ref.current === key) return;
    lapse_polled_ref.current = key;
    poll_claim();
  }, [lapsed, claim.claim_expires_at_iso, poll_claim]);

  // Latest-1on1 recording popup (video-react player + chapter strip). The
  // replay (signed URL + transcript + chapters) is fetched lazily on first
  // open — same flow as the early-retention form.
  const [show_session_video, set_show_session_video] = useState(false);
  const [replay, set_replay] = useState(null);
  const [loading_replay, set_loading_replay] = useState(false);
  // Which session's recording the modal is showing — the "Latest 1on1 session
  // done" button and the per-session "Play recording" in Recent communications
  // both drive it, so it's keyed by uuid (re-fetch when it changes).
  const [session_video_uuid, set_session_video_uuid] = useState('');
  const session_player_ref = useRef(null);
  const seek_session_video = (secs) => {
    const player = session_player_ref.current;
    if (!player) return;
    player.seek(secs);
    player.play();
  };

  // "Teacher Created 3 Month Plan" popup — the personalised-plan page embedded
  // in a modal (it reads its student_uuid from the URL, so an iframe keeps the
  // page untouched while avoiding the ERC form's open-in-new-tab behaviour).
  const [show_3m_plan, set_show_3m_plan] = useState(false);
  // "Download PDF" — reuses the shared personalised-plan PDF exporter (renders the
  // student-facing plan page in a hidden iframe and rasterises it via html2canvas +
  // jsPDF). Kept busy-guarded so the button can't be re-triggered mid-export.
  const [downloading_plan_pdf, set_downloading_plan_pdf] = useState(false);
  // "Share" — generates the plan PDF, uploads it to the CDN, then shares the
  // returned URL; busy-guarded like the PDF download (it takes a few seconds).
  const [sharing_plan, set_sharing_plan] = useState(false);

  // "Recent communications" — call transcript popup + on-demand recording. The
  // recording URL is signed server-side per request (never frozen), so play
  // fetches it fresh and opens the audio in a new tab.
  const [call_transcript, set_call_transcript] = useState(null); // {title, date, transcript}

  const open_call_transcript = (e) =>
    set_call_transcript({ title: e.title, date: e.date, transcript: e.transcript });
  // Stable closers: SalesDialog re-runs its focus effect whenever onClose changes,
  // and this form re-renders every second.
  const close_call_transcript = useCallback(() => set_call_transcript(null), []);
  const close_3m_plan = useCallback(() => set_show_3m_plan(false), []);
  const close_session_video = useCallback(() => set_show_session_video(false), []);

  // Fetch a fresh signed recording URL for the inline player (lazy, per call).
  const fetch_call_recording = (call_uuid) =>
    get_api(
      get_api_url(`/v2/sales_task_list/call_recording/${encodeURIComponent(call_uuid)}`),
      true
    ).then((res) => res?.data?.data?.recording_url || '');

  // ── "Message teacher" / "Message student" (same behaviour as the ERC form) ─
  const [msg_teacher_uuid, set_msg_teacher_uuid] = useState('');
  // undefined = pristine (shows the default template); a string = user-edited.
  const [msg_teacher_text, set_msg_teacher_text] = useState(undefined);
  const [msg_student_text, set_msg_student_text] = useState('');
  // {version, option_index} of the AI suggestion the box came from, or null.
  const [selected_suggestion, set_selected_suggestion] = useState(null);
  const [sending_teacher, set_sending_teacher] = useState(false);
  const [sending_student, set_sending_student] = useState(false);
  const [sending_sms, set_sending_sms] = useState(false);
  const [show_sms_window, set_show_sms_window] = useState(false);

  // ── Review Notes (rich text + attachments), persisted on task completion ──
  const [review_notes, set_review_notes] = useState('');
  const [attachments, set_attachments] = useState([]); // [{key, name, url}]
  const [uploading, set_uploading] = useState(false);
  const file_input_ref = useRef(null);
  // Seed the notes with a task tag once the form loads (still-open tasks only).
  const notes_seeded_ref = useRef(false);

  // ── footer actions (Done / Skip / Cancel + progress counter) ─────────────
  const history = router_history;

  const [stats, set_stats] = useState(null); // { remaining, closed_today }
  const [acting, set_acting] = useState(false);

  // The server checks ?level= against the caller's tier.
  const load_stats = () => {
    const level = url_level(window.location.search);
    const query = level ? `?${new URLSearchParams({ level }).toString()}` : '';
    get_api(get_api_url(`/v2/sales_task_list/stats${query}`), true)
      .then((res) => set_stats(res?.data?.data || null))
      .catch(() => {
        /* counter is best-effort; the form works without it */
      });
  };
  useEffect(load_stats, [uuid]);

  const go_to_list = () => history.push(LIST_PATH);

  // Navigate to the next open task within the same filtered context (see
  // next_task_query), preserving ?from=list so the back link stays. Falls back to
  // the list when nothing is left.
  const go_to_next = () => {
    const url = get_api_url(`/v2/sales_task_list/next_task?${next_task_query(uuid)}`);
    return get_api(url, true)
      .then((res) => {
        const next = res?.data?.data?.uuid;
        if (!next) {
          notify('success', 'No more pending tasks — all done!');
          go_to_list();
          return;
        }
        // Carry the browsing context forward: ?from keeps the back link (and the
        // list-scoped filters); ?search, ?level, ?scope, ?owner and ?call keep
        // the walk inside the same set — dropping any of them would make step 2 of a
        // Done-and-Next chain wider than step 1.
        const q = new URLSearchParams(window.location.search);
        const from = q.get('from');
        const next_params = new URLSearchParams({ uuid: next });
        if (from) next_params.set('from', from);
        ['search', 'level', 'scope', 'owner', 'call'].forEach((key) => {
          const value = q.get(key);
          if (value) next_params.set(key, value);
        });
        history.push(`/sales-task-form?${next_params.toString()}`);
      })
      .catch(() => {
        notify('error', 'Failed to find the next task');
      });
  };

  // ── closing: outcome on Done, reason on Skip (both terminal) ─────────────
  const [close_mode, set_close_mode] = useState('done');
  const [outcome, set_outcome] = useState('');
  const [override_minutes, set_override_minutes] = useState('');
  const [skip_reason, set_skip_reason] = useState('');
  const [skip_note, set_skip_note] = useState('');
  const [close_error, set_close_error] = useState('');
  const outcomes = useMemo(() => outcome_options(form?.outcomes), [form?.outcomes]);
  const skip_reasons = useMemo(() => skip_reason_options(form?.skip_reasons), [form?.skip_reasons]);
  const require_outcome = !!form?.require_outcome;
  const derived_talk = Number(form?.talk_time_seconds) || 0;
  const outcome_missing = require_outcome && !outcome;

  // Set once this page closed the task (or learned it is closed), before any
  // navigation, so a failed walk cannot leave the form editable.
  const [closed_here, set_closed_here] = useState(false);

  const fail_close = (e, fallback) => {
    if (e?.response?.status === 410) {
      set_closed_here(true);
      notify('info', 'This task was already closed');
      reload_form();
      return;
    }
    if (is_outcome_required_error(e)) {
      set_close_error(e?.response?.data?.reason || 'Choose an outcome before closing this task');
      return;
    }
    const not_holder = not_holder_message(e);
    notify('error', not_holder || e?.response?.data?.reason || fallback);
    // NOT_HOLDER: the hold moved; show where it went.
    if (e?.response?.status === 409) poll_claim({ any_state: true });
  };

  const complete_current = () => {
    const invalid = validate_done({
      outcome,
      derived_talk,
      override: override_minutes,
      require_outcome
    });
    if (invalid) {
      set_close_error(invalid);
      return Promise.reject(Object.assign(new Error(invalid), { handled: true }));
    }
    set_close_error('');
    const reply = {};
    if (notes_beyond_seed(review_notes, form?.task_label)) reply.notes_in_hridaya = review_notes;
    if (attachments.length) reply.attachments = attachments;
    const body = build_complete_payload(uuid, reply, {
      outcome,
      derived_talk,
      override: override_minutes
    });
    return post_api(get_api_url('/v2/sales_task_list/complete'), body, true).then((res) => {
      announce_claim_changed();
      set_closed_here(true);
      // An already-terminal task answers 200 with its own status.
      const status = res?.data?.data?.status;
      if (status && status !== 'submitted') {
        notify('info', 'This task was already closed');
        reload_form();
        return false;
      }
      return true;
    });
  };

  const done_and_next = () => {
    set_acting(true);
    complete_current()
      .then((closed) => closed && go_to_next())
      .catch((e) => !e?.handled && fail_close(e, 'Failed to complete task'))
      .finally(() => set_acting(false));
  };

  const done_and_back = () => {
    set_acting(true);
    complete_current()
      .then((closed) => {
        if (!closed) return;
        notify('success', 'Task completed');
        go_to_list();
      })
      .catch((e) => !e?.handled && fail_close(e, 'Failed to complete task'))
      .finally(() => set_acting(false));
  };

  // Skip is terminal: the first press opens the reason dialog, confirming closes
  // the task as `skipped` and moves on.
  const start_skip = () => {
    set_close_error('');
    set_close_mode('skip');
  };

  // Navigation only; nothing is closed.
  const next_only = () => {
    set_acting(true);
    go_to_next().finally(() => set_acting(false));
  };

  const cancel_skip = useCallback(() => {
    set_close_error('');
    set_close_mode('done');
  }, []);

  const confirm_skip = () => {
    if (!skip_reason) {
      set_close_error('Choose a skip reason');
      return;
    }
    set_acting(true);
    post_api(
      get_api_url('/v2/sales_task_list/skip'),
      build_skip_payload(uuid, skip_reason, skip_note),
      true
    )
      .then(() => {
        set_closed_here(true);
        notify('success', `Skipped: ${label_for(skip_reasons, skip_reason)}`);
        announce_claim_changed();
        return go_to_next();
      })
      .catch((e) => fail_close(e, 'Failed to skip task'))
      .finally(() => set_acting(false));
  };

  const auto = form?.auto_data || {};
  const { student } = auto;
  const { last_1on1_session } = auto;
  const { ai_getting_to_know } = auto;
  const timeline_events = auto.membership_timeline || [];
  const sessions_overview = auto.sessions_overview || {};
  const recent_communications = auto.recent_communications || [];
  const has_3m_plan = !!auto.has_3m_plan;
  // A lead task carries the LEAD uuid in student_uuid — there is no student row
  // behind it, and auto_data is empty. Blanking it here is what keeps every
  // student-only path in this form (profile link, session pages, call, email,
  // chat, plan sharing, the SMS window, the reminder's locked user) from acting
  // on a uuid that resolves to nothing: they all already guard on truthiness.
  const is_lead = !!form?.is_lead;
  const student_uuid = is_lead ? '' : form?.student_uuid;
  const churn = auto.is_churned_member ? auto.churned_member_auto : null;
  const details_as_of = details_age(form?.auto_data_computed_at_iso, now.getTime());
  const student_name = form?.student_name;
  const task_label = form?.task_label;
  // Server decides link eligibility; the UI only renders what it is given.
  const onboarding_link = form?.onboarding_link || '';
  const onboarding_link_expires_at_iso = form?.onboarding_link_expires_at_iso || '';
  const phone_unverified = !!form?.phone_unverified;
  // The signed-in CMS user is the only agent identity this form has, and the
  // lead templates introduce the agent by name.
  const cms_user_name = useSelector((st) => st.home.cms_user_name);

  // Share the 3-month plan: generate the plan PDF client-side (same exporter as
  // Download PDF, as a Blob), upload it to the CDN (/v2/cdn/bunny/upload — the
  // ops role passes student_uuid so the file lands under the student's identity),
  // then hand the returned public CDN URL to the native share sheet, falling back
  // to copy-to-clipboard (with the form's standard toast) when navigator.share is
  // unavailable. A dismissed share sheet (AbortError) is a no-op.
  const share_plan_url = async () => {
    if (!student_uuid || sharing_plan) return;
    set_sharing_plan(true);
    try {
      const { blob, filename } = await generatePersonalisedPlanPdfBlob(student_uuid, student_name);
      const fd = new FormData();
      fd.append('file', blob, filename);
      fd.append('filename', filename);
      fd.append('folder', 'three_month_plans');
      fd.append('student_uuid', student_uuid);
      const res = await api_with_method(
        'post',
        get_api_url('/v2/cdn/bunny/upload'),
        fd,
        true,
        'formdata'
      );
      const url = res?.data?.data?.url;
      if (!url) throw new Error('CDN upload returned no URL');

      const copy_link = async () => {
        await navigator.clipboard.writeText(url);
        notify('success', 'Plan PDF link copied to clipboard');
      };
      try {
        if (navigator.share) {
          await navigator.share({
            url,
            title: `${student_name || 'Student'}'s 3 Month Plan`
          });
        } else {
          await copy_link();
        }
      } catch (e) {
        // User dismissing the native share sheet is not an error.
        if (e?.name === 'AbortError') return;
        await copy_link();
      }
    } catch (e) {
      notify('error', e?.response?.data?.reason || 'Failed to share the 3-month plan');
    } finally {
      set_sharing_plan(false);
    }
  };

  const download_plan_pdf = async () => {
    if (!student_uuid || downloading_plan_pdf) return;
    set_downloading_plan_pdf(true);
    try {
      await downloadPersonalisedPlanPdf(student_uuid, student_name);
    } catch (e) {
      notify('error', 'Failed to download the 3-month plan PDF');
    } finally {
      set_downloading_plan_pdf(false);
    }
  };

  const submitted = form?.status === 'submitted';
  // Auto-closed: the system retired the task because the student became a member,
  // so there is nothing left to chase (goserver's student_became_member lifecycle
  // hook). The row is already gone from every open queue and the Complete
  // endpoint no-ops it, so the form must not keep offering "Done".
  const auto_closed = form?.status === 'auto_closed';
  // Auto-completed: an instant task nobody touched before its rule's expiry ran
  // out. A separate status from auto_closed on purpose — that one means the
  // reason for the task went away, this one means nobody got to it in time.
  const auto_completed = form?.status === 'auto_completed';
  // Skipped: a rep closed it with a reason (POST /skip).
  const skipped = form?.status === 'skipped';
  // Terminal = read-only. All three terminal statuses freeze the form the same
  // way; only the footer message distinguishes them. A 410 from the claim adds a
  // fourth path into the same state: the row went terminal between the list
  // render and this open, so the loaded payload's status is already stale.
  const terminal =
    closed_here ||
    submitted ||
    auto_closed ||
    auto_completed ||
    skipped ||
    claim_forces_terminal(claim) ||
    hold_forces_read_only(claim, now.getTime());
  // A tiered task this rep cannot close (claim refused or failed): lock the
  // closing and sending actions instead of failing open.
  const gate = claim_gate({
    state: claim.state,
    reason: claim.reason,
    terminal,
    tiered: !!form?.level,
    is_manager: may_assign,
    may_claim,
    me_loaded: !!sales_me
  });
  const actions_locked = terminal || !!gate || alloc_lock;
  // The skipped banner below already carries the skip reason.
  const closed_by_text = closed_by_line(form, { skip_reasons, include_skip_reason: false });

  // Hydrate Review Notes from the saved reply (or seed with a task tag for a
  // still-open task) once the form loads.
  useEffect(() => {
    if (!form || notes_seeded_ref.current) return;
    notes_seeded_ref.current = true;
    const reply = form.reply || {};
    if (typeof reply.notes_in_hridaya === 'string' && reply.notes_in_hridaya) {
      set_review_notes(reply.notes_in_hridaya);
    } else if (
      form.status !== 'submitted' &&
      form.status !== 'auto_closed' &&
      form.status !== 'auto_completed' &&
      form.status !== 'skipped'
    ) {
      set_review_notes(`<p>#SalesTaskList - ${form.task_label || ''}</p>`);
    }
    if (Array.isArray(reply.attachments)) set_attachments(reply.attachments);
  }, [form]);

  const upload_attachment = (file) => {
    set_uploading(true);
    const fd = new FormData();
    fd.append('student_uuid', student_uuid);
    fd.append('file', file);
    api_with_method(
      'post',
      get_api_url('/v2/sales_task_list/upload_attachment'),
      fd,
      true,
      'formdata'
    )
      .then((res) => {
        const att = res?.data?.data;
        if (att && att.key) set_attachments((prev) => [...prev, att]);
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to upload attachment'))
      .finally(() => set_uploading(false));
  };

  const handle_attachment_change = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) upload_attachment(file);
    e.target.value = '';
  };

  const remove_attachment = (idx) => set_attachments((prev) => prev.filter((_, i) => i !== idx));

  // Append a hashtag to the Review Notes (idempotent) so the note records which
  // channels the agent used — #phone on a call, #email on an email. The
  // #SalesTaskList tag is seeded by default when the form loads.
  const append_note_tag = (tag) => {
    set_review_notes((prev) => {
      const cur = prev || '';
      // Match the whole tag (not a prefix like #phonecall) using a boundary.
      if (new RegExp(`${tag}\\b`, 'i').test(cur.replace(/<[^>]*>/g, ' '))) return cur;
      return `${cur}<p>${tag}</p>`;
    });
  };

  // Messaging context (live fields from the form payload).
  const message_teachers = form?.message_teachers || [];
  const student_slug = form?.student_slug || '';
  const dispatch_method = form?.message_dispatch_method;
  const student_has_phone = !!form?.student_has_phone;
  // Direct-send SMS (server decides eligibility) and the WhatsApp hand-off,
  // both acting on the chat text box rather than opening another surface.
  const sms_eligible = !!form?.sms_eligible;
  const student_phone = form?.student_phone || '';
  const can_sms =
    !!student_uuid && student_has_phone && dispatch_method && dispatch_method !== 'none';

  // Off hours (student-local time outside the team call window): contact actions
  // confirm first. A lead's zone is on the form; no zone = no confirm.
  const off_hours_window = useMemo(
    () => (sales_me ? { start: sales_me.call_window_start, end: sales_me.call_window_end } : null),
    [sales_me]
  );
  const { guard: off_hours_guard, dialog: off_hours_dialog } = useOffHoursGate(
    form?.student_iana_timezone || student?.iana_timezone,
    off_hours_window
  );

  // Sends from the open SMS/WhatsApp window and the Open Chat popup get the same
  // off-hours confirm; opening them stays free.
  const sms_before_send = (run, method) =>
    off_hours_guard(method === 'whatsapp' ? 'whatsapp' : 'sms', run);
  const chat_guard_ref = useRef(off_hours_guard);
  chat_guard_ref.current = off_hours_guard;
  // The confirm dialog only renders in the main body; elsewhere a registered guard would swallow the send.
  const main_body_shown = !error && claim.state !== CLAIM_HOLDING && !claim_blocks_form(claim);
  useEffect(() => {
    if (!student_uuid || !main_body_shown) return undefined;
    return registerChatSendGuard(student_uuid, (run) => chat_guard_ref.current('chat', run));
  }, [student_uuid, main_body_shown]);

  // ── "Reminder" header button (same form as the ERC one) ─────────────────
  // Opens the /reminders create form, seeded from this task (linked to the
  // student, high priority, sales/retention department, link back here).
  const [show_reminder, set_show_reminder] = useState(false);
  const close_reminder = useCallback(() => set_show_reminder(false), []);
  const [reminder_department_uuid, set_reminder_department_uuid] = useState('');
  useEffect(() => {
    if (!student_uuid) return;
    prime_team_members();
    list_departments().then((rows) => {
      const list = rows || [];
      const dept =
        list.find((d) => (d.name || '').toLowerCase().includes('sales')) ||
        list.find((d) => (d.name || '').toLowerCase().includes('retention'));
      if (dept) set_reminder_department_uuid(dept.uuid);
    });
  }, [student_uuid]);

  const task_link = useMemo(
    () => `${window.location.origin}/sales-task-form?uuid=${encodeURIComponent(uuid)}`,
    [uuid]
  );

  const reminder_initial = useMemo(() => {
    if (!form) return undefined;
    const description = [
      `${task_label || 'Sales'} task follow-up for ${student_name || 'student'}(${student_slug})`,
      '',
      `Generated from the "${task_label || ''}" Sales Task.`,
      `Task link: ${task_link}`
    ].join('\n');
    return {
      description,
      priority: 'High',
      department_uuid: reminder_department_uuid,
      // Start unassigned (an empty array stops ReminderForm defaulting to self).
      assignee_uuids: []
    };
  }, [form, task_label, student_name, student_slug, task_link, reminder_department_uuid]);

  const reminder_locked_user = student_uuid
    ? { uuid: student_uuid, name: student_name, role: 'student' }
    : null;
  const me = current_team_member(getCookie('cms_email'));

  const handle_reminder_submit = (payload) =>
    create_reminder(payload)
      .then(() => {
        close_reminder();
        notify('success', 'Reminder created');
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Failed to create reminder'));

  // Default teacher message (mirrors the ERC template); the edited value wins.
  const default_teacher_message = `Student ${student_name || ''}(${student_slug}) sales follow-up.`;
  const teacher_message_value =
    msg_teacher_text === undefined ? default_teacher_message : msg_teacher_text;

  const send_teacher_message = () => {
    const message = teacher_message_value.trim();
    if (!msg_teacher_uuid) {
      notify('error', 'Select a teacher first');
      return;
    }
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    set_sending_teacher(true);
    sendMessageToUserUUID(msg_teacher_uuid, UserRole.TEACHER, message)
      .then((ok) => {
        notify(ok ? 'success' : 'error', ok ? 'Message sent to teacher' : 'Could not send message');
        if (ok) set_msg_teacher_text(undefined); // revert to pristine template
      })
      .catch(() => notify('error', 'Could not send message'))
      .finally(() => set_sending_teacher(false));
  };

  const open_teacher_chat = () => {
    if (!msg_teacher_uuid) {
      notify('error', 'Select a teacher first');
      return;
    }
    openChatWithUserUUID(msg_teacher_uuid, UserRole.TEACHER);
  };

  const post_ai_note = (path, body) => post_api(get_api_url(path), body, true);

  const edit_student_text = (text) => {
    set_msg_student_text(text);
    set_selected_suggestion((prev) => selection_after_edit(prev, text));
  };

  const send_student_message = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    // Every composed message carries the continue-onboarding footer. The helper
    // is idempotent, a no-op without a link, and returns '' for an empty draft
    // so an empty box can never ship a bare footer.
    const message = compose_outbound_message(msg_student_text, onboarding_link);
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    const note = ai_note_payload(uuid, selected_suggestion, 'chat', message, is_lead);
    // One id for both paths: the API may have published before it failed.
    const message_id = uuid_v4();
    const on_sent = () => {
      set_msg_student_text('');
      set_selected_suggestion(null);
      record_ai_message(post_ai_note, note);
    };
    const send_socket = (ok_text) =>
      sendMessageToUserUUID(student_uuid, UserRole.STUDENT, message, message_id).then((ok) => {
        notify(ok ? 'success' : 'error', ok ? ok_text : 'Could not send message');
        if (ok) on_sent();
      });
    const send_api = () =>
      send_task_chat(uuid, message, message_id).then(
        () => {
          notify('success', 'Message sent to student');
          on_sent();
          poll_claim();
        },
        (e) => {
          const failure = chat_send_failure(e);
          if (failure === 'fallback') return send_socket('Sent (hold not refreshed)');
          if (failure === 'terminal') {
            set_closed_here(true);
            notify('info', 'This task was already closed');
            reload_form();
            return undefined;
          }
          notify('error', e?.response?.data?.reason || 'Could not send message');
          if (failure === 'not_holder') poll_claim({ any_state: true });
          return undefined;
        }
      );
    set_sending_student(true);
    const route = chat_send_route(claim.state, { is_lead, student_uuid });
    (route === 'api' ? send_api() : send_socket('Message sent to student'))
      .catch(() => notify('error', 'Could not send message'))
      .finally(() => set_sending_student(false));
  };

  // Sends straight away — no confirmation step, matching the PRD. Clears the
  // box on success the same way Send chat does.
  const send_student_sms = () => {
    // The server appends the same footer idempotently, so a doubled link is not
    // possible whichever side gets there first.
    const message = compose_outbound_message(msg_student_text, onboarding_link);
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    set_sending_sms(true);
    post_api(
      get_api_url('/v2/sales_task_list/send_sms'),
      sms_payload(uuid, message, selected_suggestion, is_lead),
      true
    )
      .then(() => {
        notify('success', 'SMS sent');
        set_msg_student_text('');
        set_selected_suggestion(null);
      })
      .catch((e) => notify('error', e?.response?.data?.reason || 'Could not send SMS'))
      .finally(() => set_sending_sms(false));
  };

  // Hands the draft to WhatsApp Web / the desktop app — nothing is sent from
  // here, the operator sends it there.
  const open_whatsapp_handoff = () => {
    const digits = (student_phone || '').replace(/\D/g, '');
    if (!digits) {
      notify('error', 'Student has no phone number');
      return;
    }
    // The hand-off never had an empty guard; without it an empty box opens
    // WhatsApp on a bare onboarding footer.
    const message = compose_outbound_message(msg_student_text, onboarding_link);
    if (!message) {
      notify('error', 'Message is empty');
      return;
    }
    window.open(
      `https://wa.me/${digits}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener'
    );
    const note = ai_note_payload(uuid, selected_suggestion, 'whatsapp', message, is_lead);
    if (!note) return;
    // The box is kept (the rep may retry the hand-off); the server dedups repeats.
    record_ai_message(post_ai_note, note);
  };

  const open_student_chat = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    openChatWithUserUUID(student_uuid, UserRole.STUDENT);
  };

  // Call the student via the app-level Twilio outbound manager — same global
  // events the student profile's "Call User" button uses. Track the global
  // outbound-active flag so the button disables during any in-progress call.
  const [call_active, set_call_active] = useState(!!window.__twilioOutboundActive);
  useEffect(() => {
    let refresh_timer = null;
    const handler = (e) => {
      const active = !!e?.detail?.active;
      set_call_active(active);
      if (active) return;
      // Talk time is derived from Twilio's call log, which lands after hang-up.
      clearTimeout(refresh_timer);
      refresh_timer = setTimeout(() => {
        fetch_form()
          .then((data) => {
            if (!data) return;
            set_form((prev) =>
              prev ? { ...prev, talk_time_seconds: data.talk_time_seconds } : prev
            );
          })
          .catch(() => {});
      }, TALK_TIME_REFRESH_MS);
    };
    window.addEventListener('twilio-outgoing-call-active', handler);
    return () => {
      clearTimeout(refresh_timer);
      window.removeEventListener('twilio-outgoing-call-active', handler);
    };
  }, [fetch_form]);
  const call_student = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    window.dispatchEvent(
      new CustomEvent('twilio-outgoing-call-start', {
        detail: { studentUuid: student_uuid, studentName: student_name || '' }
      })
    );
    append_note_tag('#phone');
  };

  // "Send email" now opens the shared rich composer (the Support Inbox's "New
  // conversation" modal) in a popup, prefilled with the student as recipient. It
  // posts through the support create-conversation API, so the outbound email
  // lands as a proper support thread.
  const student_email = form?.student_email || '';
  const [show_email_composer, set_show_email_composer] = useState(false);
  // Agent/team pickers in the composer come from the support bootstrap; fetch it
  // lazily the first time the composer is opened (empty lists render as
  // placeholders and are optional, so this never blocks sending).
  const support_bootstrap = useSelector((s) => s.support.bootstrap);
  const support_bootstrap_status = useSelector((s) => s.support.bootstrap_status);
  const create_conversation_result = useSelector((s) => s.support.create_conversation_result);
  const composer_agents = useMemo(
    () =>
      (support_bootstrap.agents || []).map((a) => ({
        key: a.uuid,
        label: a.name,
        email: a.email
      })),
    [support_bootstrap.agents]
  );
  const composer_teams = useMemo(
    () =>
      (support_bootstrap.teams || []).map((t) => ({
        key: t.uuid || t.UUID,
        label: t.name || t.Name
      })),
    [support_bootstrap.teams]
  );
  // The Inbox picker on /support lists the team folders (bootstrap teams with a
  // 'team:' key prefix). It's UI-only — the create-conversation contract has no
  // inbox concept — so labels are all that matter; mirror /support's shape.
  const composer_inboxes = useMemo(
    () => composer_teams.map((t) => ({ key: `team:${t.key}`, label: t.label })),
    [composer_teams]
  );
  const open_email_composer = () => {
    if (!student_uuid) {
      notify('error', 'No student for this task');
      return;
    }
    if (!student_email) {
      notify('error', 'Student has no email on file');
      return;
    }
    // Load the support bootstrap (inbox/team/agent lists) if it isn't already
    // in the store — keyed on status (not list length) so a previous failure
    // retries on the next open instead of leaving the pickers empty forever.
    if (support_bootstrap_status !== 'success' && support_bootstrap_status !== 'loading') {
      dispatch(actions.get_support_bootstrap());
    }
    set_show_email_composer(true);
  };

  // ── suggested messages (inline, under the chat text box) ──────────────────
  // Goserver pre-generates a pair when the sales task is created and keeps
  // every attempt as a version, so this endpoint answers immediately. A
  // "pending" status means a generation is in flight (the server auto-enqueues
  // one when nothing is ready) — poll until it settles. Goserver owns the
  // prompt construction, so the browser never sees the external credential.
  const [playbook_draft, set_playbook_draft] = useState(null);
  // idle | loading | ready | fail | none — "ready" covers every settled
  // server status (ready / no_message / stale); the draft carries which. Stale
  // is terminal: nothing regenerates on its own, the operator asks for it.
  // "none" = no suggestion exists and none was requested; the form must load
  // without waiting on a 60-90s generation, so only a click starts one.
  const [playbook_status, set_playbook_status] = useState('idle');
  const playbook_poll_ref = useRef(null);
  const playbook_peek_ref = useRef(null);
  const playbook_deadline_ref = useRef(0);

  const stop_playbook_poll = () => {
    if (playbook_poll_ref.current) {
      clearInterval(playbook_poll_ref.current);
      playbook_poll_ref.current = null;
    }
  };
  const stop_playbook_peek = () => {
    if (playbook_peek_ref.current) {
      clearInterval(playbook_peek_ref.current);
      playbook_peek_ref.current = null;
    }
  };

  // `peek` never enqueues a generation — it only reports whether the pair on
  // screen has been invalidated by something the student did since.
  const peek_playbook = (always_apply = false) =>
    post_api(get_api_url(PLAYBOOK_URL), { task_uuid: uuid, peek: true }, true)
      .then((res) => {
        const draft = res?.data?.data;
        if (!draft) return;
        if (draft.status === 'stale') stop_playbook_peek();
        if (always_apply || draft.status === 'stale') {
          set_playbook_draft((prev) => ({ ...(prev || {}), ...draft }));
        }
      })
      .catch(() => {});

  const start_playbook_peek = () => {
    stop_playbook_peek();
    playbook_peek_ref.current = setInterval(() => peek_playbook(), PLAYBOOK_PEEK_MS);
  };

  const playbook_failed = () => {
    stop_playbook_poll();
    set_playbook_status('fail');
  };

  // Returns true when the caller should start polling.
  const apply_playbook_result = (data) => {
    const draft = data || {};
    // Only a regenerate reaches here while a pair is on screen, and it carries
    // no options — keep the current pair usable until the new one lands.
    set_playbook_draft((prev) =>
      draft.status === 'pending' && !(draft.options || []).length && prev
        ? { ...prev, status: 'pending', regenerate_remaining: draft.regenerate_remaining }
        : draft
    );
    if (draft.status === 'pending') {
      if (Date.now() > playbook_deadline_ref.current) {
        playbook_failed();
        return false;
      }
      return true;
    }
    stop_playbook_poll();
    if (draft.status === 'failed') {
      playbook_failed();
      return false;
    }
    if (draft.status === 'none') {
      set_playbook_status('none');
      return false;
    }
    set_playbook_status('ready');
    if (draft.status !== 'stale') start_playbook_peek();
    return false;
  };

  // Polls peek too: a plain request would start a fresh generation the moment
  // the attempt being watched fails, and only a click may do that.
  const poll_playbook = () => {
    post_api(get_api_url(PLAYBOOK_URL), { task_uuid: uuid, peek: true }, true)
      .then((res) => apply_playbook_result(res?.data?.data))
      .catch(playbook_failed);
  };

  // peek: read what exists without starting a generation (form open).
  const load_playbook = (regenerate = false, peek = false) => {
    stop_playbook_poll();
    stop_playbook_peek();
    set_playbook_status('loading');
    playbook_deadline_ref.current = Date.now() + PLAYBOOK_POLL_LIMIT_MS;
    const payload = { task_uuid: uuid };
    if (regenerate) payload.regenerate = true;
    else if (peek) payload.peek = true;
    post_api(get_api_url(PLAYBOOK_URL), payload, true)
      .then((res) => {
        if (apply_playbook_result(res?.data?.data)) {
          playbook_poll_ref.current = setInterval(poll_playbook, PLAYBOOK_POLL_MS);
        }
      })
      .catch((e) => {
        // A refused regenerate leaves the current pair untouched and usable.
        if (regenerate && e?.response?.status === 429) {
          notify('error', 'Regenerate limit reached');
          set_playbook_status('ready');
          start_playbook_peek();
          return;
        }
        playbook_failed();
      });
  };

  // On open only read the stored pair (peek). A missing pair is never
  // generated here — that is the operator's click.
  useEffect(() => {
    if (!uuid) return undefined;
    load_playbook(false, true);
    return () => {
      stop_playbook_poll();
      stop_playbook_peek();
    };
    // load_playbook only closes over uuid; re-running on every render would
    // restart generation on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  // Cancel only stops the wait — the server keeps generating, and the next
  // open (or the retry link) picks up whatever landed.
  const cancel_playbook_generation = () => {
    stop_playbook_poll();
    set_playbook_status('none');
  };

  // The footer lands as soon as the draft does, so the agent edits the message
  // they are actually going to send rather than having a link appear at send.
  // Composed, not footered: a blank suggestion must clear the box rather than
  // seed it with a bare link the Send guards would then accept.
  const fill_student_text = (text) => {
    const composed = compose_outbound_message(text, onboarding_link);
    set_msg_student_text(composed);
    return composed;
  };

  const select_playbook_option = (text, option_index) => {
    const composed = fill_student_text(text);
    set_selected_suggestion(
      composed ? make_selection(playbook_draft?.version, option_index) : null
    );
  };

  // Lead templates are not AI suggestions and must never be tagged.
  const select_lead_template = (text) => {
    fill_student_text(text);
    set_selected_suggestion(null);
  };

  // Fixed openers stand in for the suggestions on a lead task — there is no
  // profile for Antaryami to read (Q7). They fill the same textarea.
  const lead_templates = useMemo(
    () => lead_message_templates({ student_name, agent_name: cms_user_name, onboarding_link }),
    [student_name, cms_user_name, onboarding_link]
  );

  const playbook_options = playbook_draft?.options || [];
  const playbook_has_options = playbook_options.length === 2 && !playbook_draft?.no_message;
  const playbook_pending = playbook_status === 'loading';
  const playbook_stale = playbook_draft?.status === 'stale';
  const playbook_regen_left = playbook_draft?.regenerate_remaining;
  const playbook_regen_exhausted = playbook_regen_left === 0;
  const playbook_regen_label = () => {
    if (playbook_pending && playbook_has_options) return 'Regenerating…';
    if (typeof playbook_regen_left !== 'number') return 'Regenerate';
    return `Regenerate (${playbook_regen_left} left)`;
  };

  // ── option feedback (one vote per option, immutable once recorded) ────────
  const [feedback_panel, set_feedback_panel] = useState(null); // option index
  const [feedback_reason, set_feedback_reason] = useState('');
  const [feedback_sending, set_feedback_sending] = useState(false);

  const playbook_feedback_for = (index) =>
    (playbook_draft?.feedback || []).find((f) => f.option_index === index) || null;

  const submit_playbook_feedback = (option_index, vote, reason) => {
    if (feedback_sending) return;
    set_feedback_sending(true);
    const payload = { task_uuid: uuid, version: playbook_draft?.version, option_index, vote };
    if (reason) payload.reason = reason;
    post_api(get_api_url(`${PLAYBOOK_URL}/feedback`), payload, true)
      .then(() => {
        set_playbook_draft((prev) => ({
          ...(prev || {}),
          feedback: [...(prev?.feedback || []), { option_index, vote, reason: reason || '' }]
        }));
        set_feedback_panel(null);
        set_feedback_reason('');
      })
      .catch((e) => {
        if (e?.response?.status === 409) {
          notify('error', 'Feedback already recorded');
          peek_playbook(true);
          set_feedback_panel(null);
          set_feedback_reason('');
          return;
        }
        notify('error', e?.response?.data?.reason || 'Could not record feedback');
      })
      .finally(() => set_feedback_sending(false));
  };

  const open_feedback_panel = (index) => {
    set_feedback_panel(index);
    set_feedback_reason('');
  };
  const close_feedback_panel = () => {
    set_feedback_panel(null);
    set_feedback_reason('');
  };

  // Full session-history pages in the hridaya CMS for this student.
  // No student, no session history: an id-less link resolves to '?id=undefined'
  // and 404s. SessionList renders nothing for an empty url.
  const all_sessions_url = (which) =>
    student_uuid
      ? `/usersessions/${which}?id=${student_uuid}&user_type=STUDENT&name=${encodeURIComponent(
          student_name || ''
        )}`
      : '';

  // Open the session-recording modal for a given session uuid (the latest-1on1
  // button and each session card in Recent communications call this). Re-fetches
  // the replay only when a different session is opened.
  const open_session_video = (sessionUuid) => {
    if (!sessionUuid) return;
    set_show_session_video(true);
    if (sessionUuid === session_video_uuid && (replay || loading_replay)) return;
    set_session_video_uuid(sessionUuid);
    set_replay(null);
    set_loading_replay(true);
    const url = get_api_url(
      `/v2/sales_task_list/session_replay/${encodeURIComponent(sessionUuid)}`
    );
    get_api(url, true)
      .then((res) => {
        set_replay(res?.data?.data || {});
        set_loading_replay(false);
      })
      .catch(() => {
        set_replay({});
        set_loading_replay(false);
      });
  };

  // Built before the early returns so a failed load still offers the way back to the queue.
  const back_link = show_back_link ? (
    <button
      type="button"
      onClick={go_to_list}
      className="text-xs font-medium text-content-link hover:underline"
      style={{ ...LINK_BTN_STYLE, fontSize: 12 }}
    >
      ← Sales tasks
    </button>
  ) : null;

  if (loading) return <CustomLoading />;
  if (error) {
    return (
      <div className="flex flex-col items-start gap-2 text-[13px]">
        {back_link}
        <div style={{ color: 'var(--state-error-text)' }}>{error}</div>
      </div>
    );
  }
  if (!form) return null;

  // Claim lost. The PRD is explicit that the second rep sees "Already claimed by
  // [rep]" INSTEAD of the task opening, so this replaces the form rather than
  // sitting above it — an editable form here is how two reps end up calling the
  // same member. The way back to the queue stays available.
  // Holding another standard task: one at a time, so this one never opens.
  if (claim.state === CLAIM_HOLDING) {
    const held_href = `/sales-task-form?${new URLSearchParams({
      uuid: claim.held_uuid || '',
      ...Object.fromEntries(
        ['from', 'level', 'scope', 'owner', 'call', 'search']
          .map((k) => [k, new URLSearchParams(window.location.search).get(k)])
          .filter(([, v]) => v)
      )
    }).toString()}`;
    return (
      <div data-testid="claim-holding-panel" className="flex max-w-[720px] flex-col gap-3">
        {back_link}
        <Banner tone="warning">
          <span className="block py-1 text-[13px] font-semibold">
            Close your current task first
          </span>
          <span className="block">
            You are holding{' '}
            {[claim.held_task_label, claim.held_student_name].filter(Boolean).join(' · ') ||
              'another task'}
            . Finish or release it before opening another standard task.
          </span>
          {claim.held_uuid ? (
            <Link to={held_href} className={`mt-1.5 inline-block pb-1 ${LINK_CLASS}`}>
              Open the held task
            </Link>
          ) : null}
        </Banner>
        <Btn className="self-start" onClick={go_to_list}>
          Back to sales tasks
        </Btn>
      </div>
    );
  }

  if (claim_blocks_form(claim)) {
    return (
      <div className="flex max-w-[720px] flex-col gap-3">
        {back_link}
        <Banner tone="error">
          <span className="block py-1 text-[13px] font-semibold">{claim.reason}</span>
          <span className="block pb-1">
            This task is held by another rep, so it is no longer yours to work.
          </span>
        </Banner>
        <Btn className="self-start" onClick={go_to_list}>
          Back to sales tasks
        </Btn>
      </div>
    );
  }

  const assign_picker =
    may_assign && !terminal && !claim_blocks_form(claim) ? (
      <AssignPicker
        task_uuid={uuid}
        on_assigned={on_assigned}
        on_error={(m) => notify('error', m)}
      />
    ) : null;

  const tz = student?.iana_timezone;
  // Leads have no student row; their zone comes on the form.
  const zone = tz || form?.student_iana_timezone;
  // moment-timezone's `z` token gives the short abbreviation (PST, IST, …). A
  // member without a zone keeps the old browser-time fallback.
  let their_time = '';
  if (zone) their_time = moment(now).tz(zone).format('ddd, MMM D · h:mm:ss A z');
  else if (student && !is_lead) their_time = moment(now).format('ddd, MMM D · h:mm:ss A');
  // Same zone order as the confirm gate, so the chip and the prompt never disagree.
  const off_hours_now = offHours(
    form?.student_iana_timezone || zone,
    now.getTime(),
    off_hours_window
  );
  const created_line = createdTimeLine(form?.created_at_iso, zone);
  const location = [student?.city, student?.state, student?.country].filter(Boolean).join(', ');
  const gender_age = [student?.gender, student?.age ? `${student.age} yrs` : '']
    .filter(Boolean)
    .join(', ');
  const kind_level = kind_level_label(is_lead, form?.level);
  const priority = form?.priority ? priority_of(form) : '';
  const rule_rest = rule_line_rest(
    task_label,
    form?.rule_day,
    form?.created_at_iso || form?.created_date_iso,
    form?.task_type
  );
  const btn_size = is_phone ? 'touch' : 'md';

  const meta_items = [
    created_line ? (
      <span key="created" data-testid="task-created-time">
        {created_line}
      </span>
    ) : null,
    their_time ? (
      <span key="time" className="inline-flex flex-wrap items-center gap-1.5">
        <span>
          Their time now{' '}
          <span className="font-semibold tabular-nums text-content-primary">{their_time}</span>
        </span>
        {off_hours_now ? (
          <Chip tone="neutral" title={offHoursTooltip(off_hours_now)}>
            Outside calling hours
          </Chip>
        ) : null}
      </span>
    ) : null,
    details_as_of && !is_lead ? (
      <span key="details" data-testid="details-as-of" className="inline-flex flex-wrap gap-1.5">
        <span>
          Details as of {details_as_of.absolute} · {details_as_of.relative}
        </span>
        {terminal ? null : (
          <button
            type="button"
            onClick={refresh_details}
            disabled={refreshing_details}
            className="font-semibold text-content-link hover:underline disabled:opacity-60"
            style={{
              ...LINK_BTN_STYLE,
              fontSize: 12,
              cursor: refreshing_details ? 'default' : 'pointer'
            }}
          >
            {refreshing_details ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      </span>
    ) : null
  ].filter(Boolean);

  // Instant task: the banner stays (never dismissible): it is the only place the
  // form says why this task jumped the queue and how long it has left, and on an
  // auto-completed task it is the explanation.
  const hold_banner = shows_hold_banner(claim) ? (
    <ClaimCountdownBanner
      claim={claim}
      now_ms={now.getTime()}
      on_release={release_claim}
      on_reopen={reopen}
      releasing={releasing}
      assign_slot={claim.state === CLAIM_MINE ? assign_picker : null}
      touch={is_phone}
    />
  ) : null;

  // Call / Chat / SMS-or-WhatsApp window / Email: each opens its own surface and
  // is independent of the message box. Phones carry them in the header card.
  // Lead rows carry their own number; student_email confirms the student row loaded.
  const no_phone = !!form?.student_email && !form.is_lead && !student_has_phone;
  const contact_row = (
    <div className="flex flex-col gap-1.5">
      {no_phone ? (
        <p className="m-0 text-xs text-content-muted" data-testid="sales-task-no-phone">
          No phone number on file. Reach them by email or chat.
        </p>
      ) : null}
      <div className="grid grid-flow-col gap-2" style={{ gridAutoColumns: 'minmax(0,1fr)' }}>
        <Btn
          variant="primary"
          size={btn_size}
          block
          disabled={call_active || !student_uuid || no_phone}
          title={no_phone ? 'No phone number on file. Reach them by email or chat.' : undefined}
          onClick={() => off_hours_guard('call', call_student)}
        >
          <FaPhone size={11} style={{ transform: 'rotate(90deg)' }} aria-hidden="true" />
          {call_active ? 'On call…' : 'Call'}
        </Btn>
        <Btn size={btn_size} block disabled={!student_uuid} onClick={open_student_chat}>
          Chat
        </Btn>
        {/* Opens the same SMS/WhatsApp window as the student profile; the student's
          preference picks which one (none when dispatch is 'none' or no phone). */}
        {can_sms ? (
          <Btn size={btn_size} block onClick={() => set_show_sms_window(true)}>
            {dispatch_method === 'whatsapp' ? 'WhatsApp' : 'SMS'}
          </Btn>
        ) : null}
        <Btn size={btn_size} block disabled={!student_uuid} onClick={open_email_composer}>
          Email
        </Btn>
      </div>
    </div>
  );

  const header = (
    <header className="flex flex-col gap-2 rounded-[10px] border border-line-subtle bg-surface-card px-[18px] py-3.5 max-sm:p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {back_link}
        <div className="ml-auto flex items-center gap-2.5">
          {stats ? (
            <span className="text-xs text-content-muted">
              <span className="font-semibold text-content-primary">{stats.remaining}</span>{' '}
              remaining ·{' '}
              <span className="font-semibold" style={{ color: 'var(--state-success-text)' }}>
                {stats.closed_today}
              </span>{' '}
              closed today
            </span>
          ) : null}
          {/* Opens the /reminders create form, seeded from this task (linked to
              the student, high priority, sales/retention department, link back here). */}
          {student_uuid ? (
            <Btn
              size={is_phone ? 'touch' : 'row'}
              onClick={() => set_show_reminder(true)}
              title="Create a reminder from this task"
            >
              <FaPlus size={10} aria-hidden="true" style={{ color: 'var(--text-link-brand)' }} />
              Reminder
            </Btn>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <h1 className="m-0 min-w-0 break-words text-[18px] font-semibold leading-6">
          {student_uuid ? (
            <Link
              to={`/studentprofile?id=${encodeURIComponent(student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`capitalize ${LINK_CLASS}`}
            >
              {student_name || '—'}
            </Link>
          ) : (
            <span className="capitalize text-content-primary">{student_name || '—'}</span>
          )}
        </h1>
        {kind_level ? <Chip tone="neutral">{kind_level}</Chip> : null}
        {/* Rule that generated the task: label, then "Day N · generated date" (rule_line.js). */}
        <span className="font-semibold">{task_label || '—'}</span>
        {rule_rest ? <span className="text-xs text-content-muted">{rule_rest}</span> : null}
        {priority ? (
          <Chip
            tone={priority === 'P0' ? 'error' : 'neutral'}
            title={PRIORITY_META[priority].title}
          >
            {priority}
          </Chip>
        ) : null}
        {hold_banner && !is_phone ? <div className="ml-auto">{hold_banner}</div> : null}
      </div>

      {meta_items.length ? (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-content-muted">
          {meta_items.map((item, i) => (
            <React.Fragment key={item.key}>
              {i ? <span aria-hidden="true">·</span> : null}
              {item}
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {/* A lead has no Member card to read the number off, and the number is the
          only way to reach them — so it goes in the header. */}
      {is_lead && (student_phone || form?.student_iana_timezone) ? (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          {student_phone ? <span className="text-content-primary">{student_phone}</span> : null}
          {phone_unverified ? <PhoneUnverifiedBadge /> : null}
          {form?.student_iana_timezone ? (
            <span className="text-xs text-content-muted">{form.student_iana_timezone}</span>
          ) : null}
        </div>
      ) : null}

      {hold_banner && is_phone ? hold_banner : null}

      {gate ? (
        <Banner
          tone="warning"
          testId="claim-gate-banner"
          actions={
            <Btn size={is_phone ? 'touch' : 'row'} onClick={run_claim}>
              Retry claim
            </Btn>
          }
        >
          <span className="block font-semibold">{gate.title}</span>
          <span className="block">{gate.detail}</span>
        </Banner>
      ) : null}
      {claim_step === 'hold' && alloc_copy ? (
        <Banner
          tone="info"
          testId="alloc-hold-banner"
          actions={
            <Btn size={is_phone ? 'touch' : 'row'} onClick={accept_allocation_claim}>
              {alloc_copy.confirm_label}
            </Btn>
          }
        >
          <span className="block font-semibold">You can close this tab</span>
          <span className="block">{alloc_copy.leave_note}</span>
        </Banner>
      ) : null}
      {/* Managers who never claim (or whose claim failed open) still assign. */}
      {assign_picker && claim.state !== CLAIM_MINE ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="form-assign-row">
          <span className="text-xs text-content-muted">
            {claim.claimed_by_name || form.claimed_by_name
              ? `Held by ${claim.claimed_by_name || form.claimed_by_name}`
              : 'Not held by anyone'}
          </span>
          {assign_picker}
        </div>
      ) : null}

      {form?.is_instant ? (
        <InstantBanner
          reason={form.reason}
          created_at_iso={form.created_at_iso}
          instant_expires_at_iso={visible_instant_expiry(form, claim)}
          claimed_by_name={claim.claimed_by_name || form.claimed_by_name}
          terminal={terminal}
          now={now}
        />
      ) : null}

      {submitted ? <Banner tone="success">This task is already completed.</Banner> : null}
      {auto_closed ? (
        <Banner tone="neutral">
          This task was closed automatically — the student is now a member.
        </Banner>
      ) : null}
      {auto_completed ? (
        <Banner tone="neutral">This instant task expired untouched and was auto-completed.</Banner>
      ) : null}
      {closed_by_text ? (
        <Banner tone="neutral" testId="closed-by-banner">
          <span className="font-semibold">{closed_by_text}</span>
        </Banner>
      ) : null}
      {skipped ? (
        <Banner tone="neutral" testId="skipped-banner">
          <span className="font-semibold">
            Skipped: {label_for(skip_reasons, form.skip_reason) || 'no reason recorded'}
          </span>
          {form.skip_note ? <span className="text-content-muted"> · {form.skip_note}</span> : null}
        </Banner>
      ) : null}

      {is_phone ? contact_row : null}
    </header>
  );

  // Everything from Member to Membership timeline is built from auto_data, which
  // is empty for a lead (there is no student to compute it from); those cards are
  // left out rather than shown empty, which would read as missing data.
  const context = (
    <div className="flex min-w-0 flex-col gap-3">
      {student && !is_lead ? (
        <SectionCard title="Member">
          <div className="px-3.5 py-3">
            <Ledger
              rows={[
                ...(gender_age ? [['Gender, age', gender_age]] : []),
                ['Location', location || '—'],
                ...(tz ? [['Timezone', tz]] : []),
                ['Goals', student.goals || '—'],
                ['Health history', student.health_history || '—']
              ]}
            />
          </div>
        </SectionCard>
      ) : null}

      {is_lead ? null : (
        <>
          {/* Churned member: the early-retention auto values + teacher/class list,
              server-gated on auto_data.is_churned_member. */}
          {churn ? <ChurnedMemberSection churn={churn} student_uuid={student_uuid} /> : null}

          <SectionCard title="Sessions">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-b border-line-subtle px-3.5 py-2.5 text-[12.5px]">
              <span className="text-content-muted">Latest 1-on-1</span>
              {last_1on1_session ? (
                <>
                  <span className="font-semibold">{last_1on1_session.start_time}</span>
                  {last_1on1_session.teacher_name ? (
                    <span className="text-content-secondary">
                      with {last_1on1_session.teacher_name}
                    </span>
                  ) : null}
                  <Btn
                    size={is_phone ? 'touch' : 'row'}
                    variant="outline"
                    className="ml-auto"
                    onClick={() => open_session_video(last_1on1_session.session_uuid)}
                  >
                    <FaPlay size={9} aria-hidden="true" />
                    Watch recording
                  </Btn>
                </>
              ) : (
                <span className="text-content-muted">No 1-on-1 sessions done yet.</span>
              )}
            </div>
            {churn ? <ChurnSessionBadges churn={churn} /> : null}
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
            >
              <SessionList
                title="Last 5"
                list={sessions_overview.past}
                view_all_url={all_sessions_url('past')}
                view_all_label="All past"
              />
              <SessionList
                title="Next 5"
                list={sessions_overview.future}
                view_all_url={all_sessions_url('future')}
                view_all_label="All future"
              />
            </div>
          </SectionCard>

          {/* ERC timeline, extended with trial bookings/joins, trial resets/ends
              and membership cancellations. */}
          <SectionCard title="Membership timeline">
            <MembershipTimeline
              events={timeline_events}
              next_renewal={auto.next_renewal_date || ''}
              sessions_remaining={
                typeof auto.sessions_remaining === 'number' ? auto.sessions_remaining : undefined
              }
            />
          </SectionCard>
        </>
      )}

      <SectionCard title="Recent communications">
        <RecentCommunications
          events={recent_communications}
          onOpenTranscript={open_call_transcript}
          fetchRecording={fetch_call_recording}
          onPlaySession={open_session_video}
          onError={(m) => notify('error', m)}
        />
      </SectionCard>

      <div
        className="grid items-start gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
      >
        {is_lead ? null : (
          <SectionCard title="AI: getting to know them">
            {ai_getting_to_know && ai_getting_to_know.length > 0 ? (
              <div className="flex flex-col gap-2.5 px-3.5 py-3">
                {ai_getting_to_know.map((cat) => (
                  <div key={cat.key}>
                    <div className="mb-0.5 text-xs text-content-muted">{cat.label}</div>
                    {cat.notes.map((note) => (
                      <p key={note} className="my-0.5 text-[12.5px] italic text-content-primary">
                        “{note}”
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-3.5 py-3 text-xs text-content-muted">
                No AI summary available for this member yet.
              </div>
            )}
          </SectionCard>
        )}

        {/* The plan opens in a side panel instead of the ERC form's new tab. */}
        <SectionCard title="Teacher's 3-month plan">
          {has_3m_plan ? (
            <div className="flex flex-wrap items-center gap-2 px-3.5 py-3">
              <Btn
                size={is_phone ? 'touch' : 'row'}
                variant="outline"
                onClick={() => set_show_3m_plan(true)}
              >
                <FaExternalLinkAlt size={10} aria-hidden="true" />
                Open plan
              </Btn>
              <Btn
                size={is_phone ? 'touch' : 'row'}
                onClick={download_plan_pdf}
                disabled={downloading_plan_pdf}
              >
                <FaFilePdf size={10} aria-hidden="true" />
                {downloading_plan_pdf ? 'Preparing PDF…' : 'Download PDF'}
              </Btn>
              {student_uuid ? (
                <Btn
                  size={is_phone ? 'touch' : 'row'}
                  onClick={share_plan_url}
                  disabled={sharing_plan}
                  title="Share plan PDF link"
                  aria-label="Share plan PDF link"
                >
                  <FaShareAlt size={10} aria-hidden="true" />
                  {sharing_plan ? 'Preparing link…' : 'Share'}
                </Btn>
              ) : null}
            </div>
          ) : (
            <div className="px-3.5 py-3 text-xs text-content-muted">No 3-month plan.</div>
          )}
        </SectionCard>
      </div>
    </div>
  );

  const suggestions = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className={LABEL_CLASS}>Suggested messages</span>
        {playbook_status === 'loading' || playbook_status === 'ready' ? (
          <span title={playbook_regen_exhausted ? 'Regenerate limit reached' : undefined}>
            <Btn
              size="row"
              variant={playbook_stale ? 'outline' : 'neutral'}
              disabled={playbook_pending || playbook_regen_exhausted}
              onClick={() => load_playbook(true)}
            >
              {playbook_regen_label()}
            </Btn>
          </span>
        ) : null}
      </div>

      {playbook_stale && (playbook_has_options || playbook_draft?.no_message) ? (
        <Banner tone="warning">
          May be outdated — something changed since generation. Regenerate now for a fresh pair.
        </Banner>
      ) : null}

      {/* Nothing to fall back on yet — the whole area is the wait. */}
      {playbook_pending && !playbook_has_options ? (
        <div
          className="flex flex-col items-center gap-2 rounded-lg border border-line-subtle px-4 py-5"
          aria-live="polite"
        >
          <span
            className="inline-block h-6 w-6 animate-spin rounded-full border-2"
            style={{ borderColor: 'var(--brand-primary)', borderTopColor: 'transparent' }}
            aria-hidden="true"
          />
          <div className="text-[13px] text-content-primary">Generating message…</div>
          <div className="text-xs text-content-muted">Up to 120 seconds</div>
          <Btn size="row" onClick={cancel_playbook_generation}>
            Cancel
          </Btn>
        </div>
      ) : null}

      {/* A pair is already on screen — say a new one is coming and leave the
          current cards usable until it lands. */}
      {playbook_pending && playbook_has_options ? (
        <div
          className="rounded-lg px-3 py-2 text-xs text-content-muted"
          style={{ border: '1px dashed var(--border-subtle)' }}
        >
          New pair replaces this in place when ready
        </div>
      ) : null}

      {playbook_has_options
        ? playbook_options.map((option, index) => {
            const vote = playbook_feedback_for(index);
            return (
              <div
                key={index === 0 ? 'option-1' : 'option-2'}
                className="rounded-lg border border-line-subtle px-3 py-2.5"
              >
                <div className={`mb-1 ${LABEL_CLASS}`}>Option {index + 1}</div>
                <div className="mb-2 whitespace-pre-wrap text-[12.5px] text-content-primary">
                  {option}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="This message is good"
                      disabled={!!vote}
                      onClick={() => submit_playbook_feedback(index, 'up')}
                      style={{
                        background: 'none',
                        border: 'none',
                        margin: 0,
                        padding: 4,
                        opacity: vote && vote.vote !== 'up' ? 0.35 : 1,
                        color: vote?.vote === 'up' ? 'var(--state-success)' : 'var(--text-muted)'
                      }}
                    >
                      {vote?.vote === 'up' ? <FaThumbsUp size={13} /> : <FaRegThumbsUp size={13} />}
                    </button>
                    <button
                      type="button"
                      aria-label="This message needs work"
                      disabled={!!vote}
                      onClick={() => open_feedback_panel(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        margin: 0,
                        padding: 4,
                        opacity: vote && vote.vote !== 'down' ? 0.35 : 1,
                        color: vote?.vote === 'down' ? 'var(--state-error)' : 'var(--text-muted)'
                      }}
                    >
                      {vote?.vote === 'down' ? (
                        <FaThumbsDown size={13} />
                      ) : (
                        <FaRegThumbsDown size={13} />
                      )}
                    </button>
                    {vote ? <span className="text-xs text-content-muted">locked in</span> : null}
                  </div>
                  <Btn
                    size="row"
                    variant="outline"
                    onClick={() => select_playbook_option(option, index)}
                  >
                    Select
                  </Btn>
                </div>

                {feedback_panel === index && !vote ? (
                  <div className="mt-2.5 border-t border-line-subtle pt-2.5">
                    <div className="mb-1.5 text-xs font-semibold text-content-primary">
                      What is wrong with this message? Help us improve. *
                    </div>
                    <input
                      type="text"
                      value={feedback_reason}
                      placeholder="Tell us what to fix..."
                      onChange={(e) => set_feedback_reason(e.target.value)}
                      className="mb-2 w-full rounded-md px-2 py-1.5 text-[12.5px]"
                      style={{
                        border: '1px solid var(--input-border)',
                        background: 'var(--input-bg)',
                        color: 'var(--input-text)'
                      }}
                    />
                    <div className="flex items-center gap-2">
                      <Btn
                        size="row"
                        variant="outline"
                        disabled={!feedback_reason.trim() || feedback_sending}
                        onClick={() =>
                          submit_playbook_feedback(index, 'down', feedback_reason.trim())
                        }
                      >
                        Submit
                      </Btn>
                      <Btn size="row" onClick={close_feedback_panel}>
                        Cancel
                      </Btn>
                      <span className="text-xs text-content-muted">* required</span>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })
        : null}

      {playbook_status === 'ready' && playbook_draft?.no_message ? (
        <div className="py-1 text-[12.5px] text-content-muted">
          No message is recommended for this student right now.
        </div>
      ) : null}

      {playbook_status === 'fail' ? (
        <div className="text-[12.5px]" style={{ color: 'var(--state-error-text)' }}>
          Could not generate the message.{' '}
          <button
            type="button"
            onClick={() => load_playbook()}
            className="underline"
            style={{ ...LINK_BTN_STYLE, color: 'inherit' }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {playbook_status === 'none' ? (
        <Btn size="row" variant="ghost" className="self-start" onClick={() => load_playbook()}>
          Generate suggestions
        </Btn>
      ) : null}
    </div>
  );

  const message_student = (
    <div className="flex flex-col gap-2.5">
      {/* Suggestions (or a lead's fixed openers) fill the box below; nothing here sends. */}
      {is_lead ? (
        <LeadTemplates options={lead_templates} on_select={select_lead_template} />
      ) : (
        suggestions
      )}

      {onboarding_link ? (
        <OnboardingLinkRow
          link={onboarding_link}
          expires_at_iso={onboarding_link_expires_at_iso}
          now={now}
        />
      ) : null}

      {/* This box sends over chat, SMS or the WhatsApp hand-off only; Call /
          Email / the SMS window are the separate actions above. */}
      <div className="flex flex-col gap-2">
        <textarea
          rows={5}
          value={msg_student_text}
          placeholder="Chat message to student…"
          aria-label="Chat message to student"
          onChange={(e) => edit_student_text(e.target.value)}
          className="w-full rounded-lg px-2.5 py-2 text-[12.5px]"
          style={{
            minHeight: 120,
            resize: 'vertical',
            border: '1px solid var(--input-border)',
            background: 'var(--input-bg)',
            color: 'var(--input-text)'
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          {phone_unverified && (sms_eligible || student_phone) ? <PhoneUnverifiedBadge /> : null}
          <span className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {student_phone ? (
              <Btn
                size={is_phone ? 'touch' : 'row'}
                disabled={!msg_student_text.trim()}
                onClick={() => off_hours_guard('whatsapp', open_whatsapp_handoff)}
              >
                WhatsApp hand-off
                <FaExternalLinkAlt size={9} aria-hidden="true" />
              </Btn>
            ) : null}
            {sms_eligible ? (
              <Btn
                size={is_phone ? 'touch' : 'row'}
                disabled={sending_sms || !msg_student_text.trim() || !!gate || alloc_lock}
                onClick={() => off_hours_guard('sms', send_student_sms)}
              >
                {sending_sms ? 'Sending…' : 'Send SMS'}
              </Btn>
            ) : null}
            {/* In-app chat needs a student account; a lead has none. */}
            {is_lead ? null : (
              <Btn
                size={is_phone ? 'touch' : 'row'}
                variant="outline"
                disabled={sending_student || !student_uuid || !!gate || alloc_lock}
                onClick={() => off_hours_guard('chat', send_student_message)}
              >
                {sending_student ? 'Sending…' : 'Send chat'}
              </Btn>
            )}
          </span>
        </div>
      </div>
    </div>
  );

  // Same picker + composer + send/open-chat as the ERC form.
  const message_teacher = (
    <div className="flex flex-col gap-2">
      <div className="w-full max-w-[300px]">
        <SearchableSelect
          value={msg_teacher_uuid}
          options={message_teachers}
          placeholder="Select teacher..."
          clearable
          onChange={(val) => set_msg_teacher_uuid(val || '')}
        />
      </div>
      <textarea
        rows={6}
        value={teacher_message_value}
        aria-label="Message to teacher"
        onChange={(e) => set_msg_teacher_text(e.target.value)}
        className="w-full rounded-lg px-2.5 py-2 text-[12.5px]"
        style={{
          minHeight: 120,
          resize: 'vertical',
          border: '1px solid var(--input-border)',
          background: 'var(--input-bg)',
          color: 'var(--input-text)'
        }}
      />
      <div className="flex flex-wrap justify-end gap-2">
        <Btn
          size={is_phone ? 'touch' : 'row'}
          disabled={!msg_teacher_uuid}
          onClick={open_teacher_chat}
        >
          Open chat
        </Btn>
        <Btn
          size={is_phone ? 'touch' : 'row'}
          variant="outline"
          disabled={sending_teacher || !msg_teacher_uuid}
          onClick={send_teacher_message}
        >
          {sending_teacher ? 'Sending…' : 'Send chat'}
        </Btn>
      </div>
    </div>
  );

  // Rich-text notes + attachments, saved to the student's notes thread on
  // completion (same as the early-retention form).
  const review_notes_section = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className={LABEL_CLASS}>Review notes</span>
        {!terminal ? (
          <>
            <input
              ref={file_input_ref}
              type="file"
              style={{ display: 'none' }}
              onChange={handle_attachment_change}
            />
            <Btn
              size={is_phone ? 'touch' : 'row'}
              className="ml-auto"
              onClick={() => file_input_ref.current && file_input_ref.current.click()}
              disabled={uploading}
            >
              <FaPaperclip size={10} aria-hidden="true" />
              {uploading ? 'Uploading…' : 'Add attachment'}
            </Btn>
          </>
        ) : null}
      </div>
      {attachments.length ? (
        <div className="flex flex-col gap-1">
          {attachments.map((att, idx) => (
            <div key={att.key || idx} className="flex items-center gap-2 text-[12.5px]">
              <FaPaperclip size={10} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`min-w-0 break-all ${LINK_CLASS}`}
              >
                {att.name || att.key}
              </a>
              {!terminal ? (
                <button
                  type="button"
                  aria-label="Remove attachment"
                  onClick={() => remove_attachment(idx)}
                  className="shrink-0"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 24,
                    width: 24,
                    borderRadius: 9999,
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--surface-card)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0
                  }}
                >
                  <FaTimes size={10} />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {terminal ? (
        <div
          className="text-[12.5px] text-content-primary"
          dangerouslySetInnerHTML={{ __html: review_notes }}
        />
      ) : (
        <ReactQuill
          theme="snow"
          value={review_notes}
          onChange={set_review_notes}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Add notes…"
        />
      )}
    </div>
  );

  // Desktop keeps the outcome in the pinned footer so it is always next to Done.
  const outcome_panel = !actions_locked ? (
    <OutcomePanel
      outcomes={outcomes}
      outcome={outcome}
      on_outcome={(code) => {
        set_outcome(code);
        set_close_error('');
      }}
      require_outcome={require_outcome}
      talk_time_seconds={derived_talk}
      override_minutes={override_minutes}
      on_override={set_override_minutes}
      disabled={acting}
      error={close_mode === 'done' ? close_error : ''}
      inline={!is_phone}
    />
  ) : null;

  const done_disabled = acting || actions_locked || outcome_missing || close_mode === 'skip';
  const done_title = outcome_missing && !actions_locked ? 'Choose an outcome first' : undefined;
  // Phones: one row of Done and next / Done / Skip, Next and Cancel full width below.
  const foot_item = (wide) => (is_phone ? { flex: wide ? '2 1 0' : '1 1 0', minWidth: 0 } : null);
  const footer = (
    <div
      className={`flex flex-wrap items-center gap-2 border-t border-line-subtle bg-surface-sunken px-3.5 py-3 ${
        is_phone
          ? 'fixed inset-x-0 bottom-0 z-50 gap-1.5 px-3 pb-3.5 pt-2.5'
          : 'sticky bottom-0 z-10'
      } ${is_phone ? '' : 'rounded-[10px] border'}`}
      style={is_phone ? { boxShadow: 'var(--shadow-card)' } : undefined}
      data-testid="form-footer"
    >
      {!is_phone && outcome_panel ? (
        <div className="w-full border-b border-line-subtle pb-2.5">{outcome_panel}</div>
      ) : null}
      <span title={done_title} style={foot_item(true)}>
        <Btn
          variant="primary"
          size={btn_size}
          block={is_phone}
          onClick={done_and_next}
          disabled={done_disabled}
        >
          Done and next
        </Btn>
      </span>
      <span title={done_title} style={foot_item(false)}>
        <Btn size={btn_size} block={is_phone} onClick={done_and_back} disabled={done_disabled}>
          Done
        </Btn>
      </span>
      {!actions_locked ? (
        <span style={foot_item(false)}>
          <Btn
            size={btn_size}
            block={is_phone}
            onClick={start_skip}
            disabled={acting || close_mode === 'skip'}
          >
            Skip…
          </Btn>
        </span>
      ) : null}
      {/* A holder must close or skip; everyone else may just walk on. */}
      {actions_locked || (claim.state !== CLAIM_MINE && claim.state !== CLAIM_PENDING) ? (
        <span style={foot_item(false)}>
          <Btn size={btn_size} block={is_phone} onClick={next_only} disabled={acting}>
            Next
          </Btn>
        </span>
      ) : null}
      <Btn
        variant="ghost"
        size={is_phone ? 'md' : btn_size}
        className={is_phone ? 'w-full' : undefined}
        style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: is_phone ? 0 : 'auto' }}
        onClick={go_to_list}
        disabled={acting}
      >
        Cancel
      </Btn>
    </div>
  );

  // No inner scroll box: the page is the only scroller.
  const work_panel = (
    <aside
      aria-label="Work on this task"
      className="flex min-w-0 flex-col self-start rounded-[10px] border border-line-subtle bg-surface-card"
    >
      <div className="flex flex-col gap-2.5 px-3.5 py-3">
        {is_phone ? null : contact_row}
        <Segmented
          label="Message"
          value={message_tab}
          onChange={set_message_tab}
          fill={is_phone}
          options={[
            { value: 'student', label: 'Message student' },
            { value: 'teacher', label: 'Message teacher' }
          ]}
        />
        {/* Only one composer at a time; both keep their drafts in form state. */}
        {message_tab === 'teacher' ? message_teacher : message_student}
      </div>
      <div className="border-t border-line-subtle px-3.5 py-3">{review_notes_section}</div>
      {is_phone && outcome_panel ? (
        <div className="border-t border-line-subtle px-3.5 py-3">{outcome_panel}</div>
      ) : null}
    </aside>
  );

  return (
    <div
      className="flex flex-col gap-3.5 text-[13px] text-content-primary"
      style={is_phone ? { paddingBottom: 132 } : undefined}
    >
      {header}

      <div
        className="grid items-start gap-3.5"
        style={is_wide ? { gridTemplateColumns: 'minmax(0,1fr) 440px' } : undefined}
      >
        {context}
        {work_panel}
      </div>

      {/* Outside both columns so it stays pinned to the viewport bottom for the whole form. */}
      {footer}

      {show_sms_window && student_uuid ? (
        <SmsWindow
          student_uuid={student_uuid}
          student_name={student_name}
          close_fn={() => set_show_sms_window(false)}
          message_dispatch_method={dispatch_method}
          is_allowed_sms={dispatch_method === 'sms'}
          is_allowed_whatsapp={dispatch_method === 'whatsapp'}
          enable_reminder
          reminder_assignee_uuid={form?.assigned_to_ops_uuid}
          student_iana_timezone={form?.student_iana_timezone}
          onboarding_link={onboarding_link}
          before_send={sms_before_send}
        />
      ) : null}

      {/* Rich email composer popup (shared with the Support Inbox). Opened from
          the "Email" button; prefilled with the student as recipient. */}
      {show_email_composer ? (
        <Suspense fallback={null}>
          <NewConversationModal
            onClose={() => set_show_email_composer(false)}
            onCreated={() => {
              // The support create-conversation endpoint returns 200 even when the
              // outbound send failed (sent:false); in that case the saga already
              // toasts the failure — only claim success + tag the note when the
              // email actually went out.
              if (create_conversation_result?.sent !== false) {
                notify('success', 'Email sent to student');
                append_note_tag('#email');
              }
            }}
            agents={composer_agents}
            teams={composer_teams}
            inboxes={composer_inboxes}
            refresh={null}
            dispatch={dispatch}
            initialContactType="student"
            initialToEmail={student_email}
            initialContactName={student_name || ''}
          />
        </Suspense>
      ) : null}

      <SkipDialog
        open={close_mode === 'skip' && !actions_locked}
        subtitle={`${[student_name, task_label].filter(Boolean).join(' · ')}. The next task opens after you skip.`}
        reasons={skip_reasons}
        reason={skip_reason}
        on_reason={(code) => {
          set_skip_reason(code);
          set_close_error('');
        }}
        note={skip_note}
        on_note={set_skip_note}
        on_back={cancel_skip}
        on_confirm={confirm_skip}
        busy={acting}
        error={close_mode === 'skip' ? close_error : ''}
      />

      <SalesDialog
        open={claim_step === 'confirm' && !!alloc_copy}
        onClose={decline_allocation_claim}
        role="alertdialog"
        title={alloc_copy?.title || ''}
        describedBy="alloc-confirm-body"
        testId="alloc-claim-confirm"
        footer={
          <>
            <Btn size={btn_size} onClick={leave_for_allocated}>
              {alloc_copy?.leave_label}
            </Btn>
            <Btn size={btn_size} variant="primary" onClick={accept_allocation_claim}>
              {alloc_copy?.confirm_label}
            </Btn>
          </>
        }
      >
        <div
          id="alloc-confirm-body"
          className="px-4 py-3.5 text-[13px] leading-[1.5] text-content-secondary"
        >
          {alloc_copy?.body}
        </div>
      </SalesDialog>

      {/* Call transcript (from Recent communications), read beside the form. */}
      <SalesDialog
        open={!!call_transcript}
        onClose={close_call_transcript}
        variant="side"
        width={460}
        title="Call transcript"
        subtitle={[call_transcript?.title, call_transcript?.date].filter(Boolean).join(' · ')}
        footer={
          <Btn size={btn_size} onClick={close_call_transcript}>
            Close
          </Btn>
        }
      >
        <div
          className="px-4 py-3 text-[12.5px] text-content-primary"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {call_transcript?.transcript || 'No transcript available.'}
        </div>
      </SalesDialog>

      {/* The personalised-plan page in an iframe: it reads student_uuid from its
          own URL, so embedding keeps it untouched. */}
      <SalesDialog
        open={show_3m_plan && !!student_uuid}
        onClose={close_3m_plan}
        variant="side"
        width={1000}
        title="Teacher's 3-month plan"
        subtitle={student_name || undefined}
      >
        <iframe
          title="3 Month Plan"
          src={`/personalised-plan?student_uuid=${encodeURIComponent(student_uuid || '')}`}
          className="block w-full border-0"
          // The plan page is a light, student-facing page in both themes.
          style={{ height: '100%', minHeight: '70vh', background: 'var(--email-canvas)' }}
        />
      </SalesDialog>

      {/* Session recording: player with the chapter list and transcript beside it.
          Driven by the latest-1on1 button and each session in Recent
          communications (keyed by session_video_uuid). */}
      <SalesDialog
        open={show_session_video && !!session_video_uuid}
        onClose={close_session_video}
        width={1000}
        title="Session recording"
        headerExtra={
          <a
            href={`/sessiondetails?id=${encodeURIComponent(session_video_uuid)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap text-[12.5px] text-content-link hover:underline"
            style={{ fontWeight: 600 }}
          >
            Session details ↗
          </a>
        }
        footer={
          <Btn size={btn_size} onClick={close_session_video}>
            Close
          </Btn>
        }
      >
        <div className="grid sm:grid-cols-[minmax(0,1fr)_280px]">
          <div className="flex min-w-0 flex-col gap-3 px-4 py-4">
            {loading_replay ? (
              <div className="text-xs text-content-muted">Loading recording…</div>
            ) : null}
            {!loading_replay && replay?.replay_url ? (
              <div
                className="overflow-hidden rounded-lg"
                style={{ aspectRatio: '16 / 9', background: 'var(--surface-inverse)' }}
              >
                <CustomPlayer fill video_url={replay.replay_url} player_ref={session_player_ref} />
              </div>
            ) : null}
            {!loading_replay && !replay?.replay_url ? (
              <div className="text-xs text-content-muted">
                No recording available for this session.
              </div>
            ) : null}
          </div>
          <aside
            className="flex min-h-0 flex-col border-line-subtle max-sm:border-t sm:border-l"
            style={{ maxHeight: '62vh', overflowY: 'auto' }}
          >
            {!loading_replay && replay?.replay_url && (replay.chapters || []).length > 0 ? (
              <>
                <div className={`px-3.5 pb-1.5 pt-3 ${LABEL_CLASS}`}>Chapters</div>
                {replay.chapters.map((ch) => (
                  <button
                    key={`${ch.start_seconds}-${ch.title}`}
                    type="button"
                    onClick={() => seek_session_video(ch.start_seconds)}
                    title={ch.summary || ch.title}
                    className="grid w-full gap-2 border-t border-line-subtle px-3.5 py-2 text-left hover:bg-surface-sunken"
                    style={{
                      gridTemplateColumns: '48px minmax(0,1fr)',
                      margin: 0,
                      background: 'transparent',
                      borderLeft: 0,
                      borderRight: 0,
                      borderBottom: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <span className="text-xs tabular-nums text-content-muted">
                      {ch.start_label}
                    </span>
                    <span className="text-[12.5px] text-content-primary">{ch.title}</span>
                  </button>
                ))}
              </>
            ) : null}
            <div className={`px-3.5 pb-1.5 pt-3 ${LABEL_CLASS}`}>Transcript</div>
            <div
              className="px-3.5 pb-3 text-xs text-content-primary"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {loading_replay ? 'Loading…' : replay?.transcript || 'No transcript available.'}
            </div>
          </aside>
        </div>
      </SalesDialog>

      {off_hours_dialog}

      <SalesDialog
        open={show_reminder && !!student_uuid}
        onClose={close_reminder}
        variant="side"
        width={640}
        title="New reminder"
        subtitle={student_name || undefined}
      >
        <div className="px-4 py-3.5">
          <ReminderForm
            key={`stl-reminder-${uuid}`}
            initial={reminder_initial}
            lockedLinkedUser={reminder_locked_user}
            selfUuid={me?.uuid}
            onSubmit={handle_reminder_submit}
            onCancel={close_reminder}
            submitLabel="Create"
          />
        </div>
      </SalesDialog>
    </div>
  );
}

SalesTaskForm.propTypes = {
  uuid: PropTypes.string.isRequired,
  // Opened from the list (?from=list): the header links back to it.
  show_back_link: PropTypes.bool
};
SalesTaskForm.defaultProps = { show_back_link: false };

export default SalesTaskForm;
